import { Component, type OnInit } from "@angular/core"
import { ActivatedRoute } from '@angular/router';
import { GerenciarCotaService } from '../services/gerenciar-cota.service';
import { LocalStorageService } from '../services/localstorage.service';
import { SnackBarService } from '../services/snack-bar-service.service';
import { environment } from '../../environments/environment';

interface CompanyData {
  name: string
  code: string
  lastAdjustment: {
    date: string
    time: string
    amount: string
    user: string
  }
}

interface Vehicle {
  licensePlate: string
  fleetCode: string
  model: string
  lastAdjustment: string
  quota: string
  currentBalance: string
  status: string
  adjustment: string
  notes: string
  active: boolean
  dscSituac?: string  // Adicionar campo para situação do veículo
  cryptValue?: string
  cryptValueParent?: string
  cryptKeys?: string
  mostraValSaldo?: boolean // Flag da API para mostrar/ocultar saldo
  mostraValAjuste?: boolean // Flag da API para mostrar/ocultar ajuste
  editaValAjuste?: boolean // Flag da API para habilitar/desabilitar campo de ajuste
}

interface SubFleet {
  id: number
  codSubFrota: number,
  code: string
  description: string
  totalVehicles: number
  lastAdjustment: string
  lastQuota: string
  currentBalance: string
  qtdVehicles: number;
  adjustment: string
  codFrota: number // Código da frota pai
  vehicles: Vehicle[]
  cryptValue?: string
  cryptValueParent?: string
  cryptKeys?: string
  indOcultarVeiculos?: boolean // Campo para controlar exibição de veículos
  mostraValSaldo?: boolean // Flag da API para mostrar/ocultar saldo
  mostraValAjuste?: boolean // Flag da API para mostrar/ocultar ajuste
  editaValAjuste?: boolean // Flag da API para habilitar/desabilitar campo de ajuste
  mostraBotaoAcao?: boolean // Flag da API para expandir veículos
}

interface Fleet {
  id: number
  codFrota: number
  codMacro: number
  code: string
  description: string
  totalVehicles: number
  lastAdjustment: string
  lastQuota: string
  currentBalance: string
  adjustment: string
  subFleets: SubFleet[]
  vehicles: Vehicle[]
  cryptValue?: string
  cryptValueParent?: string
  cryptKeys?: string
  indGetCota?: number
  mostraValSaldo?: boolean // Flag da API para mostrar/ocultar saldo
  mostraValAjuste?: boolean // Flag da API para mostrar/ocultar ajuste
  editaValAjuste?: boolean // Flag da API para habilitar/desabilitar campo de ajuste
}

interface Macro {
  id: number
  code: string
  description: string
  totalVehicles: number
  lastAdjustment: string
  lastQuota: string
  currentBalance: string
  adjustment: string
  codTipoLimite: number
  codGrupoCombustivel: number
  fleets: Fleet[]
  cryptValue?: string
  cryptValueParent?: string
  cryptKeys?: string
  mostraValSaldo?: boolean // Flag da API para mostrar/ocultar saldo
  mostraValAjuste?: boolean // Flag da API para mostrar/ocultar ajuste
}

@Component({
  selector: "app-gerenciar-cota",
  templateUrl: "./gerenciar-cota.component.html",
  styleUrls: ["./gerenciar-cota.component.scss"],
})
export class GerenciarCotaComponent implements OnInit {
  displayedColumns: string[] = [
    "licensePlate",
    "fleetCode",
    "model",
    "quota",
    "currentBalance",
    "status",
    "adjustment",
    "notes",
  ]

  companyData: CompanyData = {
    name: "Empresa de Transportes LTDA",
    code: "EMP001",
    lastAdjustment: {
      date: "15/12/2023",
      time: "14:30",
      amount: "R$ 5.000,00",
      user: "João Silva",
    },
  }

  fleetData: Fleet[] = [];
  macroData: Macro[] = [];

  expandedFleets: number[] = [1]
  expandedSubFleets: number[] = []
  adjustments: { [key: string]: string } = {}
  notes: { [key: string]: string } = {}
  
  // Propriedades para controle de ajustes em tempo real
  // Mapeamento de saldos originais por subfrota
  originalSubFleetBalances: { [key: number]: number } = {}
  
  // Saldo original da frota para comparação
  originalFleetBalance: number = 0
  
  // Mapeamento de dados originais dos veículos
  originalVehicleData: { [key: string]: any } = {}
  totalAdjustments: number = 0
  adjustmentHistory: Array<{
    type: 'fleet' | 'subfleet' | 'vehicle',
    id: string,
    value: number,
    description: string
  }> = []
  
  // Propriedades para o sistema de steps
  currentStep: 'adjustments' | 'confirmation' = 'adjustments'
  confirmationColumns: string[] = ['type', 'description', 'value', 'notes']
  userGuid: string = ''

  // Propriedade para identificar o tipo do usuário (8 = macro, outros = frota)
  tipoUsuario: number = 0
  codigoEntidade: number = 0

  // Propriedades para controle do acordeão de confirmação
  expandedFleetsConfirmation: number[] = []
  
  // Observação geral para todos os ajustes
  generalObservation: string = ''

  // Configuração para máscara que permite valores negativos
  maskConfig = {
    validation: false,
    allowNegativeNumbers: true,
    decimalMarker: ',',
    thousandSeparator: '.'
  }

  // Cache de dados para tela de confirmação
  private confirmationDataCache: {
    subFleets: { [id: number]: SubFleet },
    vehicles: { [licensePlate: string]: Vehicle },
    vehicleToSubFleet: { [licensePlate: string]: number }
  } = {
    subFleets: {},
    vehicles: {},
    vehicleToSubFleet: {}
  }

    // Propriedades para lazy loading
  subFrotaPageSize = 5  // 5 subfrotas iniciais
  subFrotaCurrentPage: { [key: number]: number } = {}
  subFrotaLoading: { [key: number]: boolean } = {}
  subFrotaHasMore: { [key: number]: boolean } = {}

  veiculoPageSize = 20  // Mantido 20 veículos iniciais
  veiculoCurrentPage: { [key: number]: number } = {}
  veiculoLoading: { [key: number]: boolean } = {}
  veiculoHasMore: { [key: number]: boolean } = {}
  
  // Cache completo dos dados de veículos por subfrota
  veiculoCache: { [key: number]: any[] } = {}
  veiculoCacheLoaded: { [key: number]: boolean } = {}
  
  // Cache completo dos dados de subfrotas por frota
  subFrotaCache: { [key: number]: any[] } = {}
  subFrotaCacheLoaded: { [key: number]: boolean } = {}

  subFrotaEmpty: { [key: number]: boolean } = {}
  veiculoEmpty: { [key: number]: boolean } = {}

  // Propriedades para controle de exibição de veículos
  cmdName: string = ''
  
  // Propriedades para controle de erro
  hasError: boolean = false
  errorMessage: string = ''
  errorDetails: string = ''

  // Propriedades para controle de loading inicial
  isInitialLoading: boolean = true
  loadingErrorOccurred: boolean = false

  // Propriedades para pesquisa de frotas
  fleetSearchTerm: { [macroId: number]: string } = {}

  // Propriedades para pesquisa de subfrotas
  subFleetSearchTerm: { [fleetId: number]: string } = {}

  // Propriedades para pesquisa de veículos
  vehicleSearchTerm: { [subFleetId: number]: string } = {}

  constructor(
    private gerenciarCotaService: GerenciarCotaService,
    private localStorageService: LocalStorageService,
    private snackBarService: SnackBarService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Capturar o cmdName do localStorage
    this.cmdName = this.localStorageService.getCmdNameActually();

    // Obter o userGuid e tipo do usuário do localStorage
    const userData = this.localStorageService.getItem('userData');
    if (userData) {
      const user = JSON.parse(userData);
      this.userGuid = user.retorno?.guid || '';
      this.tipoUsuario = user.retorno?.tipo || 0;
      this.codigoEntidade = user.retorno?.codigoEntidade || 0;
    }

    // Carregar dados da API para preencher os cards
    this.carregarDadosMacro();
  }

  carregarDadosMacro(): void {
    // Ativar loading inicial
    this.isInitialLoading = true;
    this.loadingErrorOccurred = false;

    // Verificar o tipo do usuário para decidir qual fluxo seguir
    // tipo = 8 -> Usuário MACRO: codigoEntidade = codMacro
    // tipo != 8 -> Usuário FROTA: codigoEntidade = codFrota
    if (this.tipoUsuario === 8) {
      // FLUXO MACRO: codigoEntidade é o codMacro
      // 1) Chamar BuscaLimitesMacro passando codMacro
      // 2) Chamar BuscaLimitesFrota passando apenas codMacro (sem codFrota)
      this.carregarDadosMacroFluxoMacro();
    } else {
      // FLUXO FROTA: codigoEntidade é o codFrota
      // 1) Chamar BuscaLimitesDadosMacro passando codFrota para obter o codMacro
      // 2) Chamar BuscaLimitesFrota passando codMacro e codFrota
      this.carregarDadosMacroFluxoFrota();
    }
  }

  // Fluxo quando usuário é MACRO (tipo=8)
  // codigoEntidade = codMacro
  private carregarDadosMacroFluxoMacro(): void {
    const codMacro = this.codigoEntidade;

    this.gerenciarCotaService.getBuscaLimitesMacro(codMacro, this.userGuid, this.cmdName).subscribe({
      next: (response) => {
        if (!response.sucesso) {
          this.isInitialLoading = false;
          this.loadingErrorOccurred = true;
          this.errorMessage = 'Erro ao carregar dados da Macro';
          this.errorDetails = response.mensagens?.join(' ') || 'Não foi possível carregar os dados. Tente novamente.';
          console.error('Erro na API BuscaLimitesMacro:', response.mensagens);
          return;
        }

        if (response.sucesso && response.retorno && response.retorno.length > 0) {
          const macroItem = response.retorno[0];

          const dataAjuste = new Date(macroItem.ultimoAjuste);
          const dataFormatada = dataAjuste.toLocaleDateString('pt-BR');

          const saldoFormatado = macroItem.valSaldo.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });

          const macro: Macro = {
            id: macroItem.codMacro,
            code: `MACRO${macroItem.codMacro}`,
            description: macroItem.dscMacro?.trim() || 'MACRO CTF',
            totalVehicles: 0,
            lastAdjustment: dataFormatada,
            lastQuota: saldoFormatado,
            currentBalance: saldoFormatado,
            adjustment: "",
            codTipoLimite: macroItem.codTipoLimite,
            codGrupoCombustivel: macroItem.codGrupoCombustivel,
            fleets: [],
            cryptValue: macroItem.cryptValue || macroItem.CryptValue,
            cryptValueParent: macroItem.cryptValueParent || macroItem.CryptValueParent || undefined,
            cryptKeys: macroItem.cryptKeys || macroItem.CryptKeys,
            mostraValSaldo: macroItem.mostraValSaldo ?? response.mostraValSaldo,
            mostraValAjuste: macroItem.mostraValAjuste ?? response.mostraValAjuste
          };

          this.macroData = [macro];

          // Para usuário MACRO, chamar BuscaLimitesFrota sem passar codFrota (null)
          // Isso retornará TODAS as frotas da macro
          this.carregarFrotas(macroItem.codMacro, null, macroItem.codGrupoCombustivel, macroItem.codTipoLimite, this.userGuid);
        } else {
          this.isInitialLoading = false;
          this.loadingErrorOccurred = true;
          this.errorMessage = 'Nenhum dado encontrado';
          this.errorDetails = 'Não foram encontrados dados de macro para exibir.';
        }
      },
      error: (error) => {
        console.error('Erro ao carregar dados macro (fluxo macro):', error);
        this.isInitialLoading = false;
        this.loadingErrorOccurred = true;
        this.errorMessage = 'Erro ao carregar dados da Macro';
        this.errorDetails = error?.mensagens?.join(' ') || 'Falha na comunicação com o servidor. Tente novamente.';
        this.handleApiError('Erro ao carregar dados macro', error);
      }
    });
  }

  // Fluxo quando usuário é FROTA (tipo!=8)
  // codigoEntidade = codFrota
  private carregarDadosMacroFluxoFrota(): void {
    const codFrota = this.codigoEntidade;

    // Chamar BuscaLimitesDadosMacro para obter o codMacro
    this.gerenciarCotaService.getDadosMacro(codFrota, this.userGuid, this.cmdName).subscribe({
      next: (response) => {
        if (!response.sucesso) {
          this.isInitialLoading = false;
          this.loadingErrorOccurred = true;
          this.errorMessage = 'Erro ao carregar dados da Macro';
          this.errorDetails = response.mensagens?.join(' ') || 'Não foi possível carregar os dados. Tente novamente.';
          console.error('Erro na API BuscaLimitesDadosMacro:', response.mensagens);
          return;
        }

        if (response.sucesso && response.retorno && response.retorno.length > 0) {
          const macroItem = response.retorno[0];

          const dataAjuste = new Date(macroItem.ultimoAjuste);
          const dataFormatada = dataAjuste.toLocaleDateString('pt-BR');

          const saldoFormatado = macroItem.valSaldo.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });

          const macro: Macro = {
            id: macroItem.codMacro,
            code: `MACRO${macroItem.codMacro}`,
            description: macroItem.dscMacro?.trim() || 'MACRO CTF',
            totalVehicles: 0,
            lastAdjustment: dataFormatada,
            lastQuota: saldoFormatado,
            currentBalance: saldoFormatado,
            adjustment: "",
            codTipoLimite: macroItem.codTipoLimite,
            codGrupoCombustivel: macroItem.codGrupoCombustivel,
            fleets: [],
            cryptValue: macroItem.cryptValue || macroItem.CryptValue,
            cryptValueParent: macroItem.cryptValueParent || macroItem.CryptValueParent || undefined,
            cryptKeys: macroItem.cryptKeys || macroItem.CryptKeys,
            mostraValSaldo: macroItem.mostraValSaldo ?? response.mostraValSaldo,
            mostraValAjuste: macroItem.mostraValAjuste ?? response.mostraValAjuste
          };

          this.macroData = [macro];

          // Para usuário FROTA, chamar BuscaLimitesFrota passando codMacro E codFrota
          // Isso retornará apenas a frota específica do usuário
          this.carregarFrotas(macroItem.codMacro, codFrota, macroItem.codGrupoCombustivel, macroItem.codTipoLimite, this.userGuid);
        } else {
          this.isInitialLoading = false;
          this.loadingErrorOccurred = true;
          this.errorMessage = 'Nenhum dado encontrado';
          this.errorDetails = 'Não foram encontrados dados de macro para exibir.';
        }
      },
      error: (error) => {
        console.error('Erro ao carregar dados macro (fluxo frota):', error);
        this.isInitialLoading = false;
        this.loadingErrorOccurred = true;
        this.errorMessage = 'Erro ao carregar dados da Macro';
        this.errorDetails = error?.mensagens?.join(' ') || 'Falha na comunicação com o servidor. Tente novamente.';
        this.handleApiError('Erro ao carregar dados macro', error);
      }
    });
  }

  carregarFrotas(codMacro: number, codFrota: number | null, codGrupoCombustivel: number, codTipoLimite: number, userGuid: string): void {
    this.gerenciarCotaService.getBuscaLimitesFrota(codMacro, codFrota, codGrupoCombustivel, codTipoLimite, userGuid, this.cmdName).subscribe({
      next: (response) => {
        // Verificar se a API retornou erro
        if (!response.sucesso) {
          this.isInitialLoading = false;
          this.loadingErrorOccurred = true;
          this.errorMessage = 'Erro ao carregar dados da Frota';
          this.errorDetails = (response as any).mensagens?.join(' ') || 'Não foi possível carregar os dados da frota. Tente novamente.';
          console.error('Erro na API BuscaLimitesFrota:', (response as any).mensagens);
          return;
        }

        if (response.sucesso && response.retorno && response.retorno.length > 0) {
          // Se usuário for MACRO (tipo=8), processa TODAS as frotas retornadas
          // Se usuário for FROTA (tipo!=8), usa apenas a posição 0
          const frotas: Fleet[] = [];

          // Determinar quais frotas processar baseado no tipo de usuário
          const frotasParaProcessar = this.tipoUsuario === 8
            ? response.retorno
            : [response.retorno[0]]; // Frota: apenas posição 0

          // Processar as frotas
          for (const frotaItem of frotasParaProcessar) {
            const dataAjuste = new Date(frotaItem.ultimoAjuste);
            const dataFormatada = dataAjuste.toLocaleDateString('pt-BR');

            const limiteFormatado = frotaItem.valLimite.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });

            const saldoFormatado = frotaItem.valSaldo.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            });

            // Usar codFrota do item da API quando disponível
            const frotaCodFrota = frotaItem.codFrota || codFrota;

            const frota: Fleet = {
              id: frotaItem.codFrota || frotaItem.codMacro, // usar codFrota como ID único da frota
              codFrota: frotaCodFrota,
              codMacro: frotaItem.codMacro || codMacro, // codMacro da frota
              code: `FROTA${frotaCodFrota}`,
              description: `${frotaItem.dscFrota}`,
              totalVehicles: 0,
              lastAdjustment: dataFormatada,
              lastQuota: limiteFormatado,
              currentBalance: saldoFormatado,
              adjustment: "",
              subFleets: [],
              vehicles: [],
              cryptValue: frotaItem.cryptValue,
              cryptValueParent: this.tipoUsuario === 2
                ? this.macroData[0]?.cryptValue
                : this.macroData.find(m => m.id === (frotaItem.codMacro || codMacro))?.cryptValue || undefined,
              cryptKeys: frotaItem.cryptKeys,
              indGetCota: frotaItem.indGetCota,
              mostraValSaldo: frotaItem.mostraValSaldo,
              mostraValAjuste: frotaItem.mostraValAjuste,
              editaValAjuste: frotaItem.editaValAjuste
            };

            frotas.push(frota);
          }

          // Adicionar todas as frotas ao macro
          if (this.macroData.length > 0) {
            this.macroData[0].fleets = frotas;

            // Os dados da macro (lastAdjustment, currentBalance, lastQuota) já foram carregados
            // corretamente em carregarDadosMacroFluxoMacro() ou carregarDadosMacroFluxoFrota()
            // usando os dados da API buscaLimitesMacro ou buscaLimitesDadosMacro.
            // NÃO sobrescrever com dados da frota para manter os valores corretos da macro.

            // Carregar as subfrotas para cada frota
            for (const frota of frotas) {
              this.carregarSubFrotas(frota.id, userGuid);
            }

            // Inicializar o saldo original para o sistema de ajustes em tempo real
            this.initializeOriginalBalance();

            // Desativar loading inicial - dados carregados com sucesso
            this.isInitialLoading = false;
            this.loadingErrorOccurred = false;
          }
        } else {
          // Dados vazios retornados
          this.isInitialLoading = false;
          this.loadingErrorOccurred = true;
          this.errorMessage = 'Nenhum dado encontrado';
          this.errorDetails = 'Não foram encontrados dados de frota para exibir.';
        }
      },
      error: (error) => {
        console.error('Erro ao carregar frotas:', error);
        this.isInitialLoading = false;
        this.loadingErrorOccurred = true;
        this.errorMessage = 'Erro ao carregar dados da Frota';
        this.errorDetails = error?.error?.mensagens?.join(' ') || 'Falha na comunicação com o servidor. Tente novamente.';
      }
    });
  }

  toggleFleet(fleetId: number): void {
    const index = this.expandedFleets.indexOf(fleetId)
    if (index > -1) {
      this.expandedFleets.splice(index, 1)
    } else {
      this.expandedFleets.push(fleetId)
      // Carregar subfrotas apenas se ainda não foram carregadas
      this.carregarSubFrotasSeNecessario(fleetId);
    }
  }

  carregarSubFrotasSeNecessario(fleetId: number): void {
    // Encontrar a frota
    if (this.macroData.length > 0) {
      const macro = this.macroData[0];
      const frota = macro.fleets.find(f => f.id === fleetId);
      
      // Só carregar se ainda não tem subfrotas
      if (frota && frota.subFleets.length === 0) {
        // Inicializar estados de paginação se não existirem
        if (this.subFrotaCurrentPage[fleetId] === undefined) {
          this.subFrotaCurrentPage[fleetId] = -1; // -1 indica que ainda não carregou nenhuma página
          this.subFrotaHasMore[fleetId] = true;
        }
        
        // Obter o userGuid do localStorage
        const userData = this.localStorageService.getItem('userData');
        let userGuid = '';
        
        if (userData) {
          const user = JSON.parse(userData);
          userGuid = user.retorno?.guid || '';
        }
        
        this.carregarSubFrotas(fleetId, userGuid, 0, false);
      }
    }
  }

  carregarSubFrotas(fleetId: number, userGuid: string, page: number = 0, append: boolean = false): void {
    // Se já temos os dados em cache e não é o primeiro carregamento, usar cache
    if (this.subFrotaCacheLoaded[fleetId] && page > 0) {
      this.carregarSubFrotasDoCache(fleetId, page, append);
      return;
    }
    
    // Encontrar a frota e o macro correspondente
    if (this.macroData.length > 0) {
      const macro = this.macroData[0];
      const frota = macro.fleets.find(f => f.id === fleetId);
      
      if (frota) {
        // Marcar como carregando
        this.subFrotaLoading[fleetId] = true;
        
        // Obter o codigoEntidade do usuário do localStorage para usar como codFrota
        const userData = this.localStorageService.getItem('userData');
        let codigoEntidade = 2; // valor padrão caso não encontre
        
        if (userData) {
          const user = JSON.parse(userData);
          codigoEntidade = user.retorno?.codigoEntidade || 2;
        }
        
        // Usar os dados do macro para buscar as subfrotas
        this.gerenciarCotaService.getBuscaLimitesSubFrota(
          macro.id,
          frota.codFrota, // Usar o codFrota real da frota
          macro.codGrupoCombustivel,
          macro.codTipoLimite,
          userGuid,
          this.cmdName,
          frota.indGetCota // Passar IndGetCota obtido da frota
        ).subscribe({
          next: (response) => {
            this.subFrotaLoading[fleetId] = false;
         
            
            if (response.sucesso && response.retorno && response.retorno.length > 0) {
              // Armazenar todos os dados no cache na primeira chamada
              if (!this.subFrotaCacheLoaded[fleetId]) {
                this.subFrotaCache[fleetId] = response.retorno;
                this.subFrotaCacheLoaded[fleetId] = true;
                console.log(`Cache de subfrotas criado para frota ${fleetId}: ${response.retorno.length} subfrotas`);
              }
              
              // Fazer paginação no frontend usando o cache
              const startIndex = page * this.subFrotaPageSize;
              const endIndex = startIndex + this.subFrotaPageSize;
              const paginatedData = this.subFrotaCache[fleetId].slice(startIndex, endIndex);
              
              // Converter os dados da API para o formato das subfrotas
              const subFleets: SubFleet[] = paginatedData.map((subFrotaItem: any) => {
                
                // Formatar a data do último ajuste
                const dataAjuste = new Date(subFrotaItem.ultimoAjuste);
                const dataFormatada = dataAjuste.toLocaleDateString('pt-BR');
                
                // Formatar o limite (última cota)
                const limiteFormatado = subFrotaItem.valLimite.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
                
                // Formatar o saldo
                const saldoFormatado = subFrotaItem.valSaldo.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
                
                const subFleet = {
                  id: subFrotaItem.codSubFrota,
                  code: `SUB${subFrotaItem.codSubFrota}`,
                  description: subFrotaItem.dscSubFrota,
                  totalVehicles: subFrotaItem.qtdVeiculos,
                  lastAdjustment: dataFormatada,
                  lastQuota: limiteFormatado, // valLimite
                  currentBalance: saldoFormatado, // valSaldo
                  adjustment: "",
                  qtdVehicles: subFrotaItem.qtdVeiculos,
                  codFrota: frota.codFrota, // Código da frota pai
                  vehicles: [], // Array vazio por enquanto, pode ser expandido depois
                  cryptValue: subFrotaItem.cryptValue,
                  cryptValueParent: frota.cryptValue, // cryptValue da Frota pai
                  cryptKeys: subFrotaItem.cryptKeys,
                  indOcultarVeiculos: subFrotaItem.indOcultarVeiculos,
                  codSubFrota: subFrotaItem.codSubFrota,
                  mostraValSaldo: subFrotaItem.mostraValSaldo,
                  mostraValAjuste: subFrotaItem.mostraValAjuste,
                  editaValAjuste: subFrotaItem.editaValAjuste,
                  mostraBotaoAcao: subFrotaItem.mostraBotaoAcao
                };
                
                // Adicionar ao cache para confirmação
                this.confirmationDataCache.subFleets[subFleet.id] = subFleet;
                
                return subFleet;
              });
              
              // Atualizar as subfrotas da frota (append ou replace)
              if (append && frota.subFleets) {
                // Verificar se há duplicações antes de adicionar
                const existingIds = frota.subFleets.map(sf => sf.id);
                const newSubFleets = subFleets.filter(sf => !existingIds.includes(sf.id));
                console.log(`Adicionando ${newSubFleets.length} novas subfrotas (${subFleets.length} retornadas, ${existingIds.length} existentes)`);
                frota.subFleets = [...frota.subFleets, ...newSubFleets];
              } else {
                frota.subFleets = subFleets;
                console.log(`Carregando ${subFleets.length} subfrotas iniciais`);
              }
              
              // Verificar se há mais dados para carregar
              const totalItems = this.subFrotaCache[fleetId].length;
              const nextPageStartIndex = (page + 1) * this.subFrotaPageSize;
              this.subFrotaHasMore[fleetId] = nextPageStartIndex < totalItems;
              this.subFrotaCurrentPage[fleetId] = page;
              
              console.log(`Página atual: ${page}, Tem mais dados: ${this.subFrotaHasMore[fleetId]}, Total subfrotas: ${frota.subFleets.length}`);
              
              // Inicializar o saldo original da subfrota após carregar as subfrotas
              this.initializeOriginalBalance();
            } else if (!response.sucesso) {
              // API retornou sucesso = false
              this.handleApiError('Erro ao carregar subfrotas', {
                message: (response as any).mensagens?.join(', ') || 'Erro desconhecido na API de subfrotas'
              });
            } else {
              this.subFrotaHasMore[fleetId] = false;
              this.subFrotaEmpty[fleetId] = true;
            }
          },
          error: (error) => {
            this.subFrotaLoading[fleetId] = false;
            console.error('Erro ao carregar subfrotas:', error);
          }
        });
      }
    }
  }

  toggleSubFleet(subFleetId: number): void {
    const index = this.expandedSubFleets.indexOf(subFleetId)
    if (index > -1) {
      this.expandedSubFleets.splice(index, 1)
    } else {
      this.expandedSubFleets.push(subFleetId)
      // Carregar veículos quando expandir a subfrota
      this.carregarVeiculosSeNecessario(subFleetId);
    }
  }

  carregarVeiculosSeNecessario(subFleetId: number): void {
    // Encontrar a subfrota
    if (this.macroData.length > 0) {
      const macro = this.macroData[0];
      for (const frota of macro.fleets) {
        const subFrota = frota.subFleets.find(sf => sf.id === subFleetId);

        // Só carregar se ainda não tem veículos
        if (subFrota && subFrota.vehicles.length === 0) {
          // Inicializar estados de paginação se não existirem
          if (this.veiculoCurrentPage[subFleetId] === undefined) {
            this.veiculoCurrentPage[subFleetId] = -1; // -1 indica que ainda não carregou nenhuma página
            this.veiculoHasMore[subFleetId] = true;
          }

          // Obter o userGuid do localStorage
          const userData = this.localStorageService.getItem('userData');
          let userGuid = '';

          if (userData) {
            const user = JSON.parse(userData);
            userGuid = user.retorno?.guid || '';
          }

          // Passar codFrota da subfrota para a API
          this.carregarVeiculos(subFleetId, macro.codGrupoCombustivel, macro.codTipoLimite, userGuid, 0, false, subFrota.codFrota);
          break;
        }
      }
    }
  }

  carregarVeiculos(codSubfrota: number, codGrupoCombustivel: number, codTipoLimite: number, userGuid: string, page: number = 0, append: boolean = false, codFrota?: number): void {
    // Se já temos os dados em cache e não é o primeiro carregamento, usar cache
    if (this.veiculoCacheLoaded[codSubfrota] && page > 0) {
      this.carregarVeiculosDoCache(codSubfrota, page, append);
      return;
    }

    // Marcar como carregando
    this.veiculoLoading[codSubfrota] = true;

    this.gerenciarCotaService.getBuscaLimitesVeiculo(
      codSubfrota,
      codGrupoCombustivel,
      codTipoLimite,
      userGuid,
      codFrota, // Passar CodFrota para a API
      this.cmdName // Passar CmdName para a API
    ).subscribe({
        next: (response) => {
          this.veiculoLoading[codSubfrota] = false;
          
          if (response.sucesso && response.retorno && response.retorno.length > 0) {
            // Armazenar todos os dados no cache na primeira chamada
            if (!this.veiculoCacheLoaded[codSubfrota]) {
              this.veiculoCache[codSubfrota] = response.retorno;
              this.veiculoCacheLoaded[codSubfrota] = true;
              console.log(`Cache de veículos criado para subfrota ${codSubfrota}: ${response.retorno.length} veículos`);
            }
            
            // Fazer paginação no frontend usando o cache
            const startIndex = page * this.veiculoPageSize;
            const endIndex = startIndex + this.veiculoPageSize;
            const paginatedData = this.veiculoCache[codSubfrota].slice(startIndex, endIndex);
            
            // Converter os dados da API para o formato dos veículos
            const vehicles: Vehicle[] = paginatedData.map((veiculoItem: any) => {
              // Salvar dados originais do veículo
              this.originalVehicleData[veiculoItem.dscPlaca] = {
                ...veiculoItem,
                valSaldoOriginal: veiculoItem.valSaldo
              };
              
              // Formatar a data do último ajuste
              const dataAjuste = new Date(veiculoItem.ultimoAjuste);
              const dataFormatada = dataAjuste.toLocaleDateString('pt-BR');
              
              // Formatar o limite (cota)
              const cotaFormatada = veiculoItem.valLimite.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              
              // Formatar o saldo atual
              const saldoFormatado = veiculoItem.valSaldo.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              });
              
              const vehicle = {
                licensePlate: veiculoItem.dscPlaca || 'N/A',
                fleetCode: veiculoItem.codFrotaVeiculo?.toString() || 'N/A',
                model: veiculoItem.dscModelo || 'Veículo',
                lastAdjustment: dataFormatada,
                quota: cotaFormatada,
                currentBalance: saldoFormatado,
                status: veiculoItem.bloqueado ? 'Bloqueado' : 'Liberado',
                adjustment: "",
                notes: "",
                active: !veiculoItem.bloqueado, // ativo se não estiver bloqueado
                dscSituac: veiculoItem.dscSituac, // Mapear campo dscSituac
                cryptValue: veiculoItem.cryptValue,
                cryptValueParent: undefined, // Será atualizado com cryptValue da SubFrota pai
                cryptKeys: veiculoItem.cryptKeys,
                mostraValSaldo: veiculoItem.mostraValSaldo,
                mostraValAjuste: veiculoItem.mostraValAjuste,
                editaValAjuste: veiculoItem.editaValAjuste
              };
            
            // Adicionar ao cache para confirmação
            this.confirmationDataCache.vehicles[vehicle.licensePlate] = vehicle;
            this.confirmationDataCache.vehicleToSubFleet[vehicle.licensePlate] = codSubfrota;
            
            return vehicle;
            });
            
            // Atualizar os veículos da subfrota e definir o cryptValueParent
            if (this.macroData.length > 0) {
              const macro = this.macroData[0];
              for (const frota of macro.fleets) {
                const subFrota = frota.subFleets.find(sf => sf.id === codSubfrota);
                if (subFrota) {
                  // Atualizar o cryptValueParent dos veículos com o cryptValue da subfrota pai
                  vehicles.forEach(vehicle => {
                    vehicle.cryptValueParent = subFrota.cryptValue; // cryptValue da SubFrota pai
                  });
                  
                  // Atualizar os veículos da subfrota (append ou replace)
                  if (append && subFrota.vehicles) {
                    // Verificar se há duplicações antes de adicionar
                    const existingPlates = subFrota.vehicles.map(v => v.licensePlate);
                    const newVehicles = vehicles.filter(v => !existingPlates.includes(v.licensePlate));
                    console.log(`Adicionando ${newVehicles.length} novos veículos (${vehicles.length} retornados, ${existingPlates.length} existentes)`);
                    subFrota.vehicles = [...subFrota.vehicles, ...newVehicles];
                  } else {
                    subFrota.vehicles = vehicles;
                    console.log(`Carregando ${vehicles.length} veículos iniciais`);
                  }
                  
                  // Verificar se há mais dados para carregar
                  const totalItems = response.retorno.length;
                  const nextPageStartIndex = (page + 1) * this.veiculoPageSize;
                  this.veiculoHasMore[codSubfrota] = nextPageStartIndex < totalItems;
                  this.veiculoCurrentPage[codSubfrota] = page;
                  
                  console.log(`Página atual: ${page}, Tem mais dados: ${this.veiculoHasMore[codSubfrota]}, Total veículos: ${subFrota.vehicles.length}`);
                  
                  break;
                }
              }
            }
          } else if (!response.sucesso) {
            // API retornou sucesso = false
            this.handleApiError('Erro ao carregar veículos', {
              message: (response as any).mensagens?.join(', ') || 'Erro desconhecido na API de veículos'
            });
          } else {
            this.veiculoHasMore[codSubfrota] = false;
            this.veiculoEmpty[codSubfrota] = true;
          }
        },
        error: (error) => {
          this.veiculoLoading[codSubfrota] = false;
          console.error('Erro ao carregar veículos:', error);
        }
      });
  }

  // Método para carregar subfrotas do cache (sem chamar a API)
  carregarSubFrotasDoCache(fleetId: number, page: number, append: boolean): void {
    if (!this.subFrotaCache[fleetId]) {
      console.error('Cache de subfrotas não encontrado para frota:', fleetId);
      return;
    }

    // Fazer paginação no frontend usando o cache
    const startIndex = page * this.subFrotaPageSize;
    const endIndex = startIndex + this.subFrotaPageSize;
    const paginatedData = this.subFrotaCache[fleetId].slice(startIndex, endIndex);
    
    // Converter os dados do cache para o formato das subfrotas
    const subFleets: SubFleet[] = paginatedData.map((subFrotaItem: any) => {
      // Formatar a data do último ajuste
      const dataAjuste = new Date(subFrotaItem.ultimoAjuste);
      const dataFormatada = dataAjuste.toLocaleDateString('pt-BR');
      
      // Formatar o limite (última cota)
      const limiteFormatado = subFrotaItem.valLimite.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      // Formatar o saldo
      const saldoFormatado = subFrotaItem.valSaldo.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      const subFleet = {
        id: subFrotaItem.codSubFrota,
        code: `SUB${subFrotaItem.codSubFrota}`,
        description: subFrotaItem.dscSubFrota,
        totalVehicles: subFrotaItem.qtdVeiculos,
        lastAdjustment: dataFormatada,
        lastQuota: limiteFormatado,
        currentBalance: saldoFormatado,
        adjustment: "",
        qtdVehicles: subFrotaItem.qtdVeiculos,
        codFrota: fleetId,
        vehicles: [],
        cryptValue: subFrotaItem.cryptValue,
        cryptValueParent: undefined, // Será atualizado com cryptValue da Frota pai
        cryptKeys: subFrotaItem.cryptKeys,
        indOcultarVeiculos: subFrotaItem.indOcultarVeiculos,
        codSubFrota: subFrotaItem.codSubFrota,
        mostraValSaldo: subFrotaItem.mostraValSaldo,
        mostraValAjuste: subFrotaItem.mostraValAjuste,
        editaValAjuste: subFrotaItem.editaValAjuste,
        mostraBotaoAcao: subFrotaItem.mostraBotaoAcao
      };

      return subFleet;
    });
    
    // Atualizar as subfrotas da frota
    if (this.macroData.length > 0) {
      const macro = this.macroData[0];
      const frota = macro.fleets.find(f => f.id === fleetId);
      if (frota) {
        // Atualizar o cryptValueParent das subfrotas com o cryptValue da frota pai
        subFleets.forEach(subFleet => {
          subFleet.cryptValueParent = frota.cryptValue;
        });
        
        // Atualizar as subfrotas da frota (append ou replace)
        if (append && frota.subFleets) {
          // Verificar se há duplicações antes de adicionar
          const existingIds = frota.subFleets.map(sf => sf.id);
          const newSubFleets = subFleets.filter(sf => !existingIds.includes(sf.id));
          console.log(`Carregando do cache: ${newSubFleets.length} novas subfrotas (${subFleets.length} retornados, ${existingIds.length} existentes)`);
          frota.subFleets = [...frota.subFleets, ...newSubFleets];
        } else {
          frota.subFleets = subFleets;
          console.log(`Carregando do cache: ${subFleets.length} subfrotas iniciais`);
        }
        
        // Verificar se há mais dados para carregar
        const totalItems = this.subFrotaCache[fleetId].length;
        const nextPageStartIndex = (page + 1) * this.subFrotaPageSize;
        this.subFrotaHasMore[fleetId] = nextPageStartIndex < totalItems;
        this.subFrotaCurrentPage[fleetId] = page;
        
        console.log(`Página atual: ${page}, Tem mais dados: ${this.subFrotaHasMore[fleetId]}, Total subfrotas: ${frota.subFleets.length}`);
        
        // Inicializar o saldo original da subfrota após carregar as subfrotas
        this.initializeOriginalBalance();
      }
    }
  }

  // Método para carregar veículos do cache (sem chamar a API)
  carregarVeiculosDoCache(codSubfrota: number, page: number, append: boolean): void {
    if (!this.veiculoCache[codSubfrota]) {
      console.error('Cache de veículos não encontrado para subfrota:', codSubfrota);
      return;
    }

    // Fazer paginação no frontend usando o cache
    const startIndex = page * this.veiculoPageSize;
    const endIndex = startIndex + this.veiculoPageSize;
    const paginatedData = this.veiculoCache[codSubfrota].slice(startIndex, endIndex);
    
    // Converter os dados do cache para o formato dos veículos
    const vehicles: Vehicle[] = paginatedData.map((veiculoItem: any) => {
      // Salvar dados originais do veículo
      this.originalVehicleData[veiculoItem.dscPlaca] = {
        ...veiculoItem,
        valSaldoOriginal: veiculoItem.valSaldo
      };
      
      // Formatar a data do último ajuste
      const dataAjuste = new Date(veiculoItem.ultimoAjuste);
      const dataFormatada = dataAjuste.toLocaleDateString('pt-BR');
      
      // Formatar o limite (cota)
      const cotaFormatada = veiculoItem.valLimite.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      // Formatar o saldo atual
      const saldoFormatado = veiculoItem.valSaldo.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      const vehicle = {
        licensePlate: veiculoItem.dscPlaca || 'N/A',
        fleetCode: veiculoItem.codFrotaVeiculo?.toString() || 'N/A',
        model: veiculoItem.dscModelo || 'Veículo',
        lastAdjustment: dataFormatada,
        quota: cotaFormatada,
        currentBalance: saldoFormatado,
        status: veiculoItem.bloqueado ? 'Bloqueado' : 'Liberado',
        adjustment: "",
        notes: "",
        active: !veiculoItem.bloqueado,
        dscSituac: veiculoItem.dscSituac,
        cryptValue: veiculoItem.cryptValue,
        cryptValueParent: undefined,
        cryptKeys: veiculoItem.cryptKeys,
        mostraValSaldo: veiculoItem.mostraValSaldo,
        mostraValAjuste: veiculoItem.mostraValAjuste,
        editaValAjuste: veiculoItem.editaValAjuste
      };

      // Adicionar ao cache para confirmação
      this.confirmationDataCache.vehicles[vehicle.licensePlate] = vehicle;
      this.confirmationDataCache.vehicleToSubFleet[vehicle.licensePlate] = codSubfrota;

      return vehicle;
    });
    
    // Atualizar os veículos da subfrota
    if (this.macroData.length > 0) {
      const macro = this.macroData[0];
      for (const frota of macro.fleets) {
        const subFrota = frota.subFleets.find(sf => sf.id === codSubfrota);
        if (subFrota) {
          // Atualizar o cryptValueParent dos veículos com o cryptValue da subfrota pai
          vehicles.forEach(vehicle => {
            vehicle.cryptValueParent = subFrota.cryptValue;
          });
          
          // Atualizar os veículos da subfrota (append ou replace)
          if (append && subFrota.vehicles) {
            // Verificar se há duplicações antes de adicionar
            const existingPlates = subFrota.vehicles.map(v => v.licensePlate);
            const newVehicles = vehicles.filter(v => !existingPlates.includes(v.licensePlate));
            console.log(`Carregando do cache: ${newVehicles.length} novos veículos (${vehicles.length} retornados, ${existingPlates.length} existentes)`);
            subFrota.vehicles = [...subFrota.vehicles, ...newVehicles];
          } else {
            subFrota.vehicles = vehicles;
            console.log(`Carregando do cache: ${vehicles.length} veículos iniciais`);
          }
          
          // Verificar se há mais dados para carregar
          const totalItems = this.veiculoCache[codSubfrota].length;
          const nextPageStartIndex = (page + 1) * this.veiculoPageSize;
          this.veiculoHasMore[codSubfrota] = nextPageStartIndex < totalItems;
          this.veiculoCurrentPage[codSubfrota] = page;
          
          console.log(`Página atual: ${page}, Tem mais dados: ${this.veiculoHasMore[codSubfrota]}, Total veículos: ${subFrota.vehicles.length}`);
          
          break;
        }
      }
    }
  }

  // Método para tratar erros da API
  handleApiError(message: string, error: any): void {
    this.hasError = true;
    this.errorMessage = message;
    this.errorDetails = error?.message || error?.error?.message || 'Erro desconhecido';
    
    console.error('Erro na API:', {
      message: this.errorMessage,
      details: this.errorDetails,
      fullError: error
    });
  }

  // Método para tentar recarregar os dados
  retryLoadData(): void {
    this.hasError = false;
    this.errorMessage = '';
    this.errorDetails = '';
    this.loadingErrorOccurred = false;
    this.isInitialLoading = true;
    this.carregarDadosMacro();
  }

  isFleetExpanded(fleetId: number): boolean {
    return this.expandedFleets.includes(fleetId)
  }

  isSubFleetExpanded(subFleetId: number): boolean {
    return this.expandedSubFleets.includes(subFleetId)
  }

  onAdjustmentChange(key: string, value: any): void {
    // Converter valor para número
    const numericValue = this.parseCurrencyValue(value)
    
    // VALIDAÇÃO ANTES de atualizar qualquer coisa
    if (key.startsWith('vehicle-') && numericValue !== 0) {
      const vehicleId = key.replace('vehicle-', '')
      const vehicle = this.findVehicleById(vehicleId)
      
      if (vehicle) {
        const originalBalance = this.getOriginalVehicleBalance(vehicleId)
        const newBalance = originalBalance + numericValue
        
        // Validar se o ajuste resultaria em saldo negativo para o veículo
        if (newBalance < 0) {
          this.snackBarService.flashMessage(
            `Não é possível fazer um ajuste que resulte em saldo negativo para o veículo ${vehicle.licensePlate}. Saldo atual: R$ ${originalBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
            ['snackbar-error'],
            'x',
            5
          )
          
          // Limpar o campo
          this.adjustments[key] = ''
          
          // Reverter saldo da subfrota para o valor original
          this.revertSubFleetBalanceToOriginal(vehicleId)
          
          return
        }
        
        // Validar se há saldo suficiente na subfrota para o ajuste positivo
        if (numericValue > 0) {
          const subFleetId = this.findSubFleetIdForVehicle(vehicleId)
          if (subFleetId) {
            const subFleet = this.findSubFleetById(subFleetId)
            if (subFleet) {
              const subFleetBalance = this.getCurrentSubFleetBalanceForValidation(key, subFleetId)
              if (numericValue > subFleetBalance) {
                this.snackBarService.flashMessage(
                  `Saldo insuficiente na subfrota ${subFleet.description}. Saldo disponível: R$ ${subFleetBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
                  ['snackbar-error'],
                  'x',
                  5
                )
                
                // Limpar o campo
                this.adjustments[key] = ''
                
                // Reverter saldo da subfrota para o valor original
                this.revertSubFleetBalanceToOriginal(vehicleId)
                
                return
              }
            }
          }
        }
      }
    } else if (key.startsWith('subfleet-') && numericValue !== 0) {
      const subFleetId = parseInt(key.replace('subfleet-', ''))
      const subFleet = this.findSubFleetById(subFleetId)
      
      if (subFleet) {
        const originalBalance = this.getOriginalSubFleetBalance(subFleetId)
        const newBalance = originalBalance + numericValue
        
        // Validar se o ajuste resultaria em saldo negativo para a subfrota
        if (newBalance < 0) {
          this.snackBarService.flashMessage(
            `Não é possível fazer um ajuste que resulte em saldo negativo para a subfrota ${subFleet.description}. Saldo atual: R$ ${originalBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
            ['snackbar-error'],
            'x',
            5
          )
          
          // Limpar o campo
          this.adjustments[key] = ''
          return
        }
        
        // Validar se o ajuste positivo na subfrota causaria saldo negativo na frota
        if (numericValue > 0) {
          // Calcular saldo atual da frota (considerando ajustes já feitos, exceto este)
          const currentFleetBalance = this.getCurrentFleetBalanceForValidation(key)
          const newFleetBalance = currentFleetBalance - numericValue
          
          if (newFleetBalance < 0) {
            this.snackBarService.flashMessage(
              `Não é possível fazer este ajuste pois resultaria em saldo negativo para a frota. Saldo disponível na frota: R$ ${currentFleetBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
              ['snackbar-error'],
              'x',
              5
            )
            
            // Limpar o campo
            this.adjustments[key] = ''
            
            // Reverter saldo da frota para o valor original
            this.revertFleetBalanceToOriginal()
            
            return
          }
        }
      }
    }
    
    // Se passou na validação, atualizar o valor
    this.adjustments[key] = value
    
    // Encontrar ajuste anterior se existir
    const existingAdjustmentIndex = this.adjustmentHistory.findIndex(adj => adj.id === key)
    
    if (existingAdjustmentIndex !== -1) {
      // Atualizar ajuste existente
      const oldValue = this.adjustmentHistory[existingAdjustmentIndex].value
      this.totalAdjustments = this.totalAdjustments - oldValue + numericValue
      this.adjustmentHistory[existingAdjustmentIndex].value = numericValue
    } else {
      // Adicionar novo ajuste apenas se o valor não for zero
      if (numericValue !== 0) {
        this.totalAdjustments += numericValue
        
        // Determinar tipo e descrição baseado na chave
        let type: 'fleet' | 'subfleet' | 'vehicle' = 'vehicle'
        let description = ''
        
        if (key.startsWith('fleet-')) {
          type = 'fleet'
          const fleetId = key.replace('fleet-', '')
          const fleet = this.findFleetById(parseInt(fleetId))
          description = fleet ? `Frota: ${fleet.description}` : `Frota ID: ${fleetId}`
        } else if (key.startsWith('subfleet-')) {
          type = 'subfleet'
          const subFleetId = key.replace('subfleet-', '')
          const subFleet = this.findSubFleetById(parseInt(subFleetId))
          description = subFleet ? `Subfrota: ${subFleet.description}` : `Subfrota ID: ${subFleetId}`
        } else if (key.startsWith('vehicle-')) {
          type = 'vehicle'
          const vehicleId = key.replace('vehicle-', '')
          const vehicle = this.findVehicleById(vehicleId)
          description = vehicle ? `Veículo: ${vehicle.licensePlate}` : `Veículo ID: ${vehicleId}`
        }
        
        this.adjustmentHistory.push({
          type,
          id: key,
          value: numericValue,
          description
        })
      }
    }
    
    // Atualizar saldo da subfrota específica (só se passou na validação)
    this.updateSubFleetBalance(key)
  }

  // Novo método para validar ajustes quando o usuário sair do campo
  onAdjustmentBlur(key: string, value: any): void {
    const numericValue = this.parseCurrencyValue(value)
    
    if (key.startsWith('vehicle-')) {
      const vehicleId = key.replace('vehicle-', '')
      const vehicle = this.findVehicleById(vehicleId)
      
      if (vehicle && numericValue !== 0) {
        const originalBalance = this.getOriginalVehicleBalance(vehicleId)
        const newBalance = originalBalance + numericValue
        
        // Validar se o ajuste resultaria em saldo negativo
        if (newBalance < 0) {
          this.snackBarService.flashMessage(
            `Não é possível fazer um ajuste que resulte em saldo negativo para o veículo ${vehicle.licensePlate}. Saldo atual: R$ ${originalBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
            ['snackbar-error'],
            'x',
            5
          )
          
          this.clearAdjustment(key)
          return
        }
        
        // Validar se há saldo suficiente na subfrota para o ajuste positivo
        if (numericValue > 0) {
          const subFleetId = this.findSubFleetIdForVehicle(vehicleId)
          if (subFleetId) {
            const subFleet = this.findSubFleetById(subFleetId)
            if (subFleet) {
              const subFleetBalance = this.parseCurrencyValue(subFleet.currentBalance)
              if (numericValue > subFleetBalance) {
                this.snackBarService.flashMessage(
                  `Saldo insuficiente na subfrota ${subFleet.description}. Saldo disponível: R$ ${subFleetBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
                  ['snackbar-error'],
                  'x',
                  5
                )
                
                this.clearAdjustment(key)
                return
              }
            }
          }
        }
      }
    } else if (key.startsWith('subfleet-')) {
      const subFleetId = parseInt(key.replace('subfleet-', ''))
      const subFleet = this.findSubFleetById(subFleetId)
      
      if (subFleet && numericValue !== 0) {
        const originalBalance = this.getOriginalSubFleetBalance(subFleetId)
        const newBalance = originalBalance + numericValue
        
        // Validar se o ajuste resultaria em saldo negativo para a subfrota
        if (newBalance < 0) {
          this.snackBarService.flashMessage(
            `Não é possível fazer um ajuste que resulte em saldo negativo para a subfrota ${subFleet.description}. Saldo atual: R$ ${originalBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
            ['snackbar-error'],
            'x',
            5
          )
          
          this.clearAdjustment(key)
          return
        }
        
        // Validar se o ajuste positivo na subfrota causaria saldo negativo na frota
        if (numericValue > 0) {
          const fleetBalance = this.originalFleetBalance
          const newFleetBalance = fleetBalance - numericValue
          
          if (newFleetBalance < 0) {
            this.snackBarService.flashMessage(
              `Não é possível fazer este ajuste pois resultaria em saldo negativo para a frota. Saldo disponível na frota: R$ ${fleetBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
              ['snackbar-error'],
              'x',
              5
            )
            
            this.clearAdjustment(key)
            return
          }
        }
      }
    } else if (key.startsWith('fleet-')) {
      const fleetId = parseInt(key.replace('fleet-', ''))
      const fleet = this.findFleetById(fleetId)
      
      if (fleet && numericValue !== 0) {
        const originalBalance = this.getOriginalFleetBalance(fleetId)
        const newBalance = originalBalance + numericValue
        
        // Validar se o ajuste resultaria em saldo negativo
        if (newBalance < 0) {
          this.snackBarService.flashMessage(
            `Não é possível fazer um ajuste que resulte em saldo negativo para a frota ${fleet.description}. Saldo atual: R$ ${originalBalance.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`,
            ['snackbar-error'],
            'x',
            5
          )
          
          this.clearAdjustment(key)
          return
        }
      }
    }
  }





  onNotesChange(key: string, value: string): void {
    this.notes[key] = value
  }

  // Método para limpar um ajuste específico
  private clearAdjustment(key: string): void {
    this.adjustments[key] = ''
    
    // Remover do histórico de ajustes
    const existingAdjustmentIndex = this.adjustmentHistory.findIndex(adj => adj.id === key)
    if (existingAdjustmentIndex !== -1) {
      const oldValue = this.adjustmentHistory[existingAdjustmentIndex].value
      this.totalAdjustments -= oldValue
      this.adjustmentHistory.splice(existingAdjustmentIndex, 1)
    }
    
    // Atualizar saldos
    this.updateSubFleetBalance(key)
  }


  getQuotaClass(quota: string): string {
    const quotaValue = Number.parseFloat(quota.replace(/\./g, "").replace(",", "."))
    if (quotaValue > 0) return "quota-positive"
    if (quotaValue < 0) return "quota-negative"
    return "quota-neutral"
  }

  // Método para verificar se um valor (string formatada) é negativo
  isNegativeValue(value: string): boolean {
    if (!value) return false;
    const numericValue = Number.parseFloat(value.replace(/\./g, "").replace(",", "."));
    return numericValue < 0;
  }

  // Método para verificar se o saldo é negativo
  isNegativeBalance(balance: string): boolean {
    if (!balance || balance === "NaN" || balance === "R$NaN") return false;
    const numericValue = Number.parseFloat(balance.replace(/\./g, "").replace(",", "."));
    return numericValue < 0;
  }

  getStatusClass(status: string): string {
    if (status === 'Bloqueado') return 'status-bloqueado';
    if (status === 'Liberado') return 'status-liberado';
    return status === "Ativo" ? "status-active" : "status-inactive";
  }


  // ============ MÉTODOS DE EXIBIÇÃO BASEADOS NAS FLAGS DA API ============

  // Método para verificar se deve ocultar saldo da MACRO
  // Usa flag mostraValSaldo retornada pela API
  deveOcultarSaldoMacro(macro: Macro): boolean {
    return macro.mostraValSaldo === false;
  }

  // Método para verificar se deve ocultar o campo de ajuste/cota da MACRO
  // Usa flag mostraValAjuste retornada pela API
  deveOcultarAjusteMacro(macro: Macro): boolean {
    return macro.mostraValAjuste === false;
  }

  // Método para verificar se deve mostrar saldo da FROTA
  // Usa flag mostraValSaldo retornada pela API
  deveOcultarSaldoFrota(fleet: Fleet): boolean {
    // Se mostraValSaldo é false ou undefined, oculta
    return fleet.mostraValSaldo === false;
  }

  // Método para verificar se deve mostrar o campo de ajuste da FROTA
  // Usa flag mostraValAjuste retornada pela API
  deveOcultarAjusteFrota(fleet: Fleet): boolean {
    // Se mostraValAjuste é false ou undefined, oculta
    return fleet.mostraValAjuste === false;
  }

  // Método para verificar se deve desabilitar campo de ajuste da FROTA
  // Usa flag editaValAjuste retornada pela API
  deveDesabilitarAjusteFrota(fleet: Fleet): boolean {
    // Se editaValAjuste é false ou undefined, desabilita
    return fleet.editaValAjuste === false;
  }

  // Método para verificar se deve ocultar saldo da SUBFROTA
  // Usa flag mostraValSaldo retornada pela API
  deveOcultarSaldoSubFrota(subFleet: SubFleet): boolean {
    return subFleet.mostraValSaldo === false;
  }

  // Método para verificar se deve ocultar ajuste da SUBFROTA
  // Usa flag mostraValAjuste retornada pela API
  deveOcultarAjusteSubFrota(subFleet: SubFleet): boolean {
    return subFleet.mostraValAjuste === false;
  }

  // Método para verificar se deve desabilitar campo de ajuste da SUBFROTA
  // Usa flag editaValAjuste retornada pela API
  deveDesabilitarAjusteSubFrota(subFleet: SubFleet): boolean {
    // Se editaValAjuste é false ou undefined, desabilita
    return subFleet.editaValAjuste === false;
  }

  // Método para verificar se deve ocultar saldo do VEÍCULO
  // Usa flag mostraValSaldo retornada pela API
  deveOcultarSaldoVeiculo(vehicle: Vehicle): boolean {
    return vehicle.mostraValSaldo === false;
  }

  // Método para verificar se deve ocultar ajuste do VEÍCULO
  // Usa flag mostraValAjuste retornada pela API
  deveOcultarAjusteVeiculo(vehicle: Vehicle): boolean {
    return vehicle.mostraValAjuste === false;
  }

  // Método para verificar se deve desabilitar campo de ajuste do VEÍCULO
  // Usa flag editaValAjuste retornada pela API
  deveDesabilitarAjusteVeiculo(vehicle: Vehicle): boolean {
    // Se editaValAjuste é false ou undefined, desabilita
    return vehicle.editaValAjuste === false;
  }

  // Método para verificar se pode expandir subfrota para mostrar veículos
  // Usa flag mostraBotaoAcao retornada pela API (opcional)
  podeExpandirSubFrota(subFleet: SubFleet): boolean {
    // Se mostraBotaoAcao não está definida ou é true, permite expandir
    // Se mostraBotaoAcao é false, não permite expandir
    if (subFleet.mostraBotaoAcao === false) {
      return false;
    }
    return true;
  }

  // Método para filtrar subfrotas com base no termo de pesquisa
  getFilteredSubFleets(fleet: Fleet): SubFleet[] {
    if (!fleet.subFleets || fleet.subFleets.length === 0) {
      return [];
    }

    const searchTerm = this.subFleetSearchTerm[fleet.id];
    if (!searchTerm || searchTerm.trim() === '') {
      return fleet.subFleets;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return fleet.subFleets.filter(subFleet => {
      const codSubFrota = subFleet.codSubFrota?.toString().toLowerCase() || '';
      const description = subFleet.description?.toLowerCase() || '';
      return codSubFrota.includes(lowerSearchTerm) || description.includes(lowerSearchTerm);
    });
  }

  // Método para filtrar frotas com base no termo de pesquisa
  getFilteredFleets(macro: Macro): Fleet[] {
    if (!macro.fleets || macro.fleets.length === 0) {
      return [];
    }

    const searchTerm = this.fleetSearchTerm[macro.id];
    if (!searchTerm || searchTerm.trim() === '') {
      return macro.fleets;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return macro.fleets.filter(fleet => {
      const codFrota = fleet.codFrota?.toString().toLowerCase() || '';
      const description = fleet.description?.toLowerCase() || '';
      return codFrota.includes(lowerSearchTerm) || description.includes(lowerSearchTerm);
    });
  }

  // Método para verificar se deve exibir o input de pesquisa de frotas
  shouldShowFleetSearch(macro: Macro): boolean {
    return macro.fleets && macro.fleets.length > 6;
  }

  // Método para verificar se deve exibir o input de pesquisa de subfrotas
  shouldShowSubFleetSearch(fleet: Fleet): boolean {
    return fleet.subFleets && fleet.subFleets.length > 6;
  }

  // Método para filtrar veículos com base no termo de pesquisa
  getFilteredVehicles(subFleet: SubFleet): Vehicle[] {
    if (!subFleet.vehicles || subFleet.vehicles.length === 0) {
      return [];
    }

    const searchTerm = this.vehicleSearchTerm[subFleet.id];
    if (!searchTerm || searchTerm.trim() === '') {
      return subFleet.vehicles;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    return subFleet.vehicles.filter(vehicle => {
      const licensePlate = vehicle.licensePlate?.toLowerCase() || '';
      const fleetCode = vehicle.fleetCode?.toLowerCase() || '';
      const model = vehicle.model?.toLowerCase() || '';
      return licensePlate.includes(lowerSearchTerm) ||
             fleetCode.includes(lowerSearchTerm) ||
             model.includes(lowerSearchTerm);
    });
  }

  exportReport(): void {
    console.log("Exportando relatório...")
  }

  // Método para atualizar o saldo de uma subfrota específica
  private updateSubFleetBalance(key: string): void {
    if (this.macroData.length === 0) return

    const macro = this.macroData[0]
    if (macro.fleets.length === 0) return

    // Se é ajuste direto da frota, apenas atualiza o saldo dessa frota específica
    if (key.startsWith('fleet-')) {
      const fleetId = parseInt(key.replace('fleet-', ''))
      this.updateFleetBalanceById(fleetId)
      return
    }

    const fleet = macro.fleets[0]

    // Determinar qual subfrota deve ser atualizada
    let targetSubFleetId: number | null = null

    if (key.startsWith('subfleet-')) {
      // Se é ajuste da própria subfrota
      targetSubFleetId = parseInt(key.replace('subfleet-', ''))
    } else if (key.startsWith('vehicle-')) {
      // Se é ajuste de veículo, encontrar a subfrota pai
      targetSubFleetId = this.findSubFleetIdForVehicle(key.replace('vehicle-', ''))
    }

    if (targetSubFleetId && fleet.subFleets.length > 0) {
      const subFleet = fleet.subFleets.find(sf => sf.id === targetSubFleetId)
      
      if (subFleet && this.originalSubFleetBalances[targetSubFleetId]) {
        // Calcular ajustes diretos da subfrota (soma normal)
        const directSubFleetAdjustments = this.adjustmentHistory
          .filter(adj => adj.id.startsWith('subfleet-') && adj.id === `subfleet-${targetSubFleetId}`)
          .reduce((sum, adj) => sum + adj.value, 0)
        
        // Calcular ajustes dos veículos (transferência inversa)
        // Quando veículo ganha saldo (+10), subfrota perde saldo (-10)
        // Quando veículo perde saldo (-10), subfrota ganha saldo (+10)
        const vehicleTransferAdjustments = this.adjustmentHistory
          .filter(adj => {
            if (adj.id.startsWith('vehicle-')) {
              const vehicleSubFleetId = this.findSubFleetIdForVehicle(adj.id.replace('vehicle-', ''))
              return vehicleSubFleetId === targetSubFleetId
            }
            return false
          })
          .reduce((sum, adj) => sum - adj.value, 0) // Nota o sinal negativo para inverter
        
        // Calcular novo saldo: saldo original + ajustes diretos + transferências dos veículos
        const newBalance = this.originalSubFleetBalances[targetSubFleetId] + directSubFleetAdjustments + vehicleTransferAdjustments
        
        // Atualizar o saldo da subfrota
        subFleet.currentBalance = newBalance.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }
    }
    
    // Atualizar saldo da frota se houve ajuste na subfrota
    this.updateFleetBalance()
  }

  // Método para atualizar o saldo da frota com base nos ajustes das subfrotas
  private updateFleetBalance(): void {
    if (this.macroData.length === 0) return
    
    const macro = this.macroData[0]
    if (macro.fleets.length === 0) return
    
    const fleet = macro.fleets[0]
    
    // Calcular ajustes diretos da frota (se houver no futuro)
    const directFleetAdjustments = this.adjustmentHistory
      .filter(adj => adj.id.startsWith('fleet-'))
      .reduce((sum, adj) => sum + adj.value, 0)
    
    // Calcular transferências das subfrotas para a frota
    // Quando subfrota ganha saldo (+100), frota perde saldo (-100)
    // Quando subfrota perde saldo (-100), frota ganha saldo (+100)
    const subFleetTransferAdjustments = this.adjustmentHistory
      .filter(adj => adj.id.startsWith('subfleet-'))
      .reduce((sum, adj) => sum - adj.value, 0) // Sinal negativo para inverter
    
    // Calcular novo saldo da frota usando o saldo original salvo
    const newFleetBalance = this.originalFleetBalance + directFleetAdjustments + subFleetTransferAdjustments
    
    // Atualizar o saldo da frota
    fleet.currentBalance = newFleetBalance.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Método para atualizar o saldo de uma frota específica pelo ID
  private updateFleetBalanceById(fleetId: number): void {
    if (this.macroData.length === 0) return

    const macro = this.macroData[0]
    if (macro.fleets.length === 0) return

    let fleet: Fleet | undefined

    // Se tipoUsuario === 2, sempre usa a primeira frota
    if (this.tipoUsuario === 2) {
      fleet = macro.fleets[0]
    } else {
      // Para outros tipos (como 8), encontrar a frota específica pelo ID
      fleet = macro.fleets.find(f => f.id === fleetId)
    }

    if (!fleet) return

    // Obter saldo original dessa frota específica
    const originalBalance = this.getOriginalFleetBalanceById(fleet.id)

    // Calcular ajustes diretos dessa frota específica
    const directFleetAdjustments = this.adjustmentHistory
      .filter(adj => adj.id === `fleet-${fleet!.id}`)
      .reduce((sum, adj) => sum + adj.value, 0)

    // Calcular transferências das subfrotas dessa frota para a frota
    const subFleetTransferAdjustments = this.adjustmentHistory
      .filter(adj => {
        if (!adj.id.startsWith('subfleet-')) return false
        const subFleetId = parseInt(adj.id.replace('subfleet-', ''))
        // Verificar se essa subfrota pertence a esta frota
        return fleet!.subFleets.some(sf => sf.id === subFleetId)
      })
      .reduce((sum, adj) => sum - adj.value, 0)

    // Calcular novo saldo da frota
    const newFleetBalance = originalBalance + directFleetAdjustments + subFleetTransferAdjustments

    // Atualizar o saldo da frota
    fleet.currentBalance = newFleetBalance.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Método para obter o saldo original de uma frota específica
  private getOriginalFleetBalanceById(fleetId: number): number {
    // Se tiver salvo no cache de saldos originais
    if (this.originalSubFleetBalances[fleetId]) {
      return this.originalSubFleetBalances[fleetId]
    }

    // Tentar obter da frota diretamente
    if (this.macroData.length > 0) {
      const macro = this.macroData[0]
      const fleet = macro.fleets.find(f => f.id === fleetId)
      if (fleet) {
        const balance = this.parseCurrencyValue(fleet.currentBalance)
        // Salvar no cache para uso futuro
        this.originalSubFleetBalances[fleetId] = balance
        return balance
      }
    }

    return 0
  }

  // Método para encontrar o ID da subfrota pai de um veículo
  private findSubFleetIdForVehicle(vehicleId: string): number | null {
    // Primeiro tentar encontrar nos dados carregados
    if (this.macroData.length > 0) {
      const macro = this.macroData[0]
      for (const fleet of macro.fleets) {
        for (const subFleet of fleet.subFleets) {
          const vehicle = subFleet.vehicles.find(v => v.licensePlate === vehicleId)
          if (vehicle) {
            return subFleet.id
          }
        }
      }
    }
    
    // Se não encontrar, verificar no cache de mapeamento veículo -> subfrota
    if (this.confirmationDataCache.vehicleToSubFleet[vehicleId]) {
      return this.confirmationDataCache.vehicleToSubFleet[vehicleId]
    }
    
    return null
  }

  // Método para obter o total do saldo original de todas as subfrotas
  getTotalOriginalBalance(): number {
    return Object.values(this.originalSubFleetBalances).reduce((sum, balance) => sum + balance, 0)
  }

  resetChanges(): void {
    this.adjustments = {}
    this.notes = {}
    this.totalAdjustments = 0
    this.adjustmentHistory = []
    
    // Restaurar saldos originais de todas as subfrotas
    if (this.macroData.length > 0 && this.macroData[0].fleets.length > 0) {
      const fleet = this.macroData[0].fleets[0]
      for (const subFleet of fleet.subFleets) {
        if (this.originalSubFleetBalances[subFleet.id]) {
          subFleet.currentBalance = this.originalSubFleetBalances[subFleet.id].toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        }
      }
    }
    
    console.log("Alterações desfeitas")
  }

  applyAdjustments(): void {
    if (this.adjustmentHistory.length === 0) {
      console.log("Nenhum ajuste para aplicar")
      return
    }
    
    this.currentStep = 'confirmation'
  }

  // Método para resetar e voltar ao step de ajustes
  resetToAdjustments(): void {
    this.currentStep = 'adjustments'
    this.resetChanges()
    this.generalObservation = '' // Resetar observação geral
    
    // Recarregar dados para garantir que estão atualizados
    this.recarregarDadosTela()
  }

  confirmAdjustments(): void {
    console.log("Confirmando ajustes...")
    
    // Criar JSON no formato especificado para o salvarAjuste
    const payload = this.createSalvarAjustePayload()
    
    console.log("JSON para SalvarAjuste:", JSON.stringify(payload, null, 2))
    
    this.gerenciarCotaService.saveAdjustments(payload, this.userGuid).subscribe({
      next: (response) => {
        if (response.sucesso === false) {
          // Exibir mensagens de erro do backend
          this.snackBarService.flashMessage(
            response.mensagens[0],
            ['snackbar-error'],
            'x',
            8
          )
        } else {
          console.log("Ajustes salvos com sucesso:", response)
          
          // Exibir mensagem de sucesso
          this.snackBarService.flashMessage(
            'Ajustes consolidados com sucesso!',
            ['snackbar-success'],
            '✓',
            4
          )
          
          // Voltar para o step de ajustes
          this.currentStep = 'adjustments'
          
          // Resetar alterações
          this.resetChanges()
          
          // Recarregar todos os dados da tela
          this.recarregarDadosTela()
        }
      },
      error: (error) => {
        console.error("Erro ao salvar ajustes:", error)
        
        // Exibir mensagem de erro
        this.snackBarService.flashMessage(
          'Erro ao consolidar ajustes. Tente novamente.',
          ['snackbar-error'],
          'x',
          5
        )
      }
    })
  }

  // Método para recarregar todos os dados da tela após consolidação
  private recarregarDadosTela(): void {
    console.log("Recarregando dados da tela...")
    
    // Limpar dados atuais
    this.macroData = []
    this.fleetData = []
    this.expandedFleets = [1]
    this.expandedSubFleets = []
    this.originalSubFleetBalances = {}
    this.originalFleetBalance = 0
    this.originalVehicleData = {}
    
    // Limpar estados de paginação
    this.subFrotaCurrentPage = {}
    this.subFrotaLoading = {}
    this.subFrotaHasMore = {}
    this.veiculoCurrentPage = {}
    this.veiculoLoading = {}
    this.veiculoHasMore = {}
    this.veiculoCache = {}
    this.veiculoCacheLoaded = {}
    this.subFrotaCache = {}
    this.subFrotaCacheLoaded = {}
    
    // Limpar cache de confirmação
    this.confirmationDataCache = {
      subFleets: {},
      vehicles: {},
      vehicleToSubFleet: {}
    }
    
    // Recarregar dados do macro
    this.carregarDadosMacro()
  }

  // Método para carregar mais subfrotas
  loadMoreSubFrotas(fleetId: number): void {
    if (this.subFrotaLoading[fleetId] || !this.subFrotaHasMore[fleetId]) {
      return;
    }
    
    const userData = this.localStorageService.getItem('userData');
    let userGuid = '';
    
    if (userData) {
      const user = JSON.parse(userData);
      userGuid = user.retorno?.guid || '';
    }
    
    const nextPage = (this.subFrotaCurrentPage[fleetId] || 0) + 1;
    this.carregarSubFrotas(fleetId, userGuid, nextPage, true);
  }

  // Método para carregar mais veículos
  loadMoreVeiculos(subFrotaId: number): void {
    if (this.veiculoLoading[subFrotaId] || !this.veiculoHasMore[subFrotaId]) {
      return;
    }
    
    const userData = this.localStorageService.getItem('userData');
    let userGuid = '';
    
    if (userData) {
      const user = JSON.parse(userData);
      userGuid = user.retorno?.guid || '';
    }
    
    // Encontrar os dados necessários para carregar veículos
    if (this.macroData.length > 0) {
      const macro = this.macroData[0];
      const nextPage = (this.veiculoCurrentPage[subFrotaId] || 0) + 1;
      
      this.carregarVeiculos(
        subFrotaId,
        macro.codGrupoCombustivel,
        macro.codTipoLimite,
        userGuid,
        nextPage,
        true
      );
    }
  }

  // Método para verificar se deve mostrar botão "Carregar mais" para subfrotas
  shouldShowLoadMoreSubFrotas(fleetId: number): boolean {
    return this.subFrotaHasMore[fleetId] && !this.subFrotaLoading[fleetId];
  }

  // Método para verificar se deve mostrar botão "Carregar mais" para veículos
  shouldShowLoadMoreVeiculos(subFrotaId: number): boolean {
    return this.veiculoHasMore[subFrotaId] && !this.veiculoLoading[subFrotaId];
  }

  // Método para verificar se está carregando subfrotas
  isLoadingSubFrotas(fleetId: number): boolean {
    return this.subFrotaLoading[fleetId] || false;
  }

  // Método para verificar se está carregando veículos
  isLoadingVeiculos(subFrotaId: number): boolean {
    return this.veiculoLoading[subFrotaId] || false;
  }

  // Método para obter dados formatados para a tabela de confirmação
  getConfirmationData(): any[] {
    return this.adjustmentHistory.map(adj => ({
      type: this.getTypeLabel(adj.type),
      description: adj.description,
      value: adj.value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
      notes: this.notes[adj.id] || ''
    }))
  }

  // Método para obter o label do tipo
  private getTypeLabel(type: 'fleet' | 'subfleet' | 'vehicle'): string {
    switch (type) {
      case 'fleet': return 'Frota'
      case 'subfleet': return 'Subfrota'
      case 'vehicle': return 'Veículo'
      default: return type
    }
  }

  // Método para criar o payload no formato correto para ConsolidaAtualizacaoDeCotas
  private createSalvarAjustePayload(): any {
    const listLimiteFrotaTO: any[] = []
    const listLimiteSubFrotaTO: any[] = []
    const listLimiteVeiculoTO: any[] = []
    
    // Processar ajustes de frotas
    this.adjustmentHistory
      .filter(adj => adj.type === 'fleet')
      .forEach(adj => {
        const fleetId = parseInt(adj.id.replace('fleet-', ''))
        const fleet = this.findFleetById(fleetId)
        
        if (fleet) {
          const originalBalance = this.originalSubFleetBalances[fleetId] || 0

          const fleetData = {
            "CodMacro": this.tipoUsuario === 2 ? this.macroData[0]?.id : fleet.codMacro,
            "CodFrota": fleet.codFrota, // Usar o codFrota real da API
            "DscFrota": fleet.description,
            "IndGetCota": 1,
            "IndGetSubFrotaCota": 1,
            "IndSetSubFrotaCota": 1,
            "ValSaldo": originalBalance,
            "ValLimite": null,
            "ValAjusteSaldo": adj.value,
            "DscObservacao": this.notes[adj.id] || "",
            "UltimoAjuste": new Date(),
            "CodAcesso": 1,
            "CodTipoEntidade": 5, // Tipo frota
            "CodEntidadeOrigem": this.tipoUsuario === 2 ? 2 : fleet.codMacro, // tipoUsuario 2 = fixo 2, senão codMacro da frota
            "CodTipoEntidadeOrigem": this.tipoUsuario === 2 ? 2 : 8, // Tipo Macro
            "CodEntidadeDestino": this.tipoUsuario === 2 ? fleetId : fleet.codFrota, // tipoUsuario 2 = fixo 5, senão codFrota
            "CodTipoEntidadeDestino": this.tipoUsuario == 2 ? 5 : 2, // Tipo Frota
            "NumProtocolo": 0,
            "CodPeriodicidade": 6,
            "CodVeiculo": null,
            "CryptValue": fleet.cryptValue || null, // cryptValue da Frota
            "CryptKeys": fleet.cryptKeys || null, // cryptKeys da Frota
            "CryptValueParent": this.tipoUsuario === 2
              ? (this.macroData[0]?.cryptValue || null)
              : (this.macroData.find(m => m.id === fleet.codMacro)?.cryptValue || null) // cryptValue da Macro (pai)
          }

          // Adicionar campos genéricos usando a função
          const fleetDataWithGenericFields = this.addGenericFields(fleetData)
          listLimiteFrotaTO.push(fleetDataWithGenericFields)
        }
      })
    
    // Processar ajustes de subfrotas
    this.adjustmentHistory
      .filter(adj => adj.type === 'subfleet')
      .forEach(adj => {
        const subFleetId = parseInt(adj.id.replace('subfleet-', ''))
        const subFleet = this.findSubFleetById(subFleetId)
        
        if (subFleet) {
          const originalBalance = this.originalSubFleetBalances[subFleetId] || 0
          
          const subFleetData = {
            "CodFrota": this.findFleetIdForSubFleet(subFleetId),
            "CodSubFrota": subFleetId,
            "DscSubFrota": subFleet.description,
            "ValSaldo": originalBalance,
            "ValLimite": null,
            "QtdVeiculos": subFleet.qtdVehicles,
            "UltimoAjuste": new Date(),
            "ValAjusteSaldo": adj.value,
            "CodAcesso": 1,
            "CodTipoEntidade": null, // Tipo subfrota
            "CodEntidadeOrigem": this.findFleetIdForSubFleet(subFleetId),
            "CodTipoEntidadeOrigem": 2, // Tipo frota
            "CodEntidadeDestino": subFleetId,
            "CodTipoEntidadeDestino": 6, // Tipo subfrota
            "NumProtocolo": 0,
            "CodPeriodicidade": 6,
            "CodVeiculo": null,
            "IndTipoConta": "N",
            "DscPeriodicidade": "Mensal",
            "CryptValue": subFleet.cryptValue,
            "CryptValueParent": subFleet.cryptValueParent, // cryptValue da Frota pai
            "CryptKeys": subFleet.cryptKeys
          }
          
          // Adicionar campos genéricos usando a função
          const subFleetDataWithGenericFields = this.addGenericFields(subFleetData)
          listLimiteSubFrotaTO.push(subFleetDataWithGenericFields)
        }
      })
    
    // Processar apenas os veículos que tiveram ajustes diretos
    this.adjustmentHistory
      .filter(adj => adj.type === 'vehicle')
      .forEach(adj => {
        const vehicleId = adj.id.replace('vehicle-', '')
        const originalData = this.originalVehicleData[vehicleId]
        
        if (originalData) {
          // Encontrar o veículo correspondente para obter os dados de criptografia
          const vehicle = this.findVehicleById(vehicleId)
          
          // Usar dados originais da API e apenas alterar os campos necessários
          const vehicleData = { ...originalData }
          vehicleData.ValSaldo = originalData.valSaldoOriginal
          vehicleData.ValAjusteSaldo = adj.value
          vehicleData.DscObservacao = this.notes[adj.id] || ""
          
          vehicleData.codEntidadeDestino = vehicleData.codVeiculo;
          vehicleData.codEntidadeOrigem = vehicleData.codSubFrota;
          vehicleData.codTipoEntidadeOrigem = 6;
          vehicleData.codTipoEntidadeDestino = 7
          
          // Adicionar campos de criptografia
          if (vehicle) {
            vehicleData.CryptValue = vehicle.cryptValue;
            vehicleData.CryptValueParent = vehicle.cryptValueParent; // cryptValue da SubFrota pai
            vehicleData.CryptKeys = vehicle.cryptKeys;
          }
          
          // Adicionar campos genéricos usando a função
          const vehicleDataWithGenericFields = this.addGenericFields(vehicleData)
          listLimiteVeiculoTO.push(vehicleDataWithGenericFields)
        }
      })
    
    // Processar ajustes de subfrotas - apenas veículos que tiveram ajustes individuais
    this.adjustmentHistory
      .filter(adj => adj.type === 'subfleet')
      .forEach(adj => {
        const subFleetId = parseInt(adj.id.replace('subfleet-', ''))
        const subFleet = this.findSubFleetById(subFleetId)
        
        if (subFleet) {
          // Para subfrotas, incluir apenas veículos que tiveram ajustes individuais
          subFleet.vehicles.forEach(vehicle => {
            const vehicleAdjustment = this.getVehicleAdjustment(vehicle.licensePlate)
            
            // Só incluir se o veículo teve ajuste individual
            if (vehicleAdjustment > 0) {
              const originalData = this.originalVehicleData[vehicle.licensePlate]
              
              if (originalData) {
                // Usar dados originais da API e apenas alterar os campos necessários
                const vehicleData = { ...originalData }
                
                vehicleData.ValSaldo = originalData.valSaldoOriginal
                vehicleData.ValAjusteSaldo = vehicleAdjustment
                vehicleData.DscObservacao = this.notes[`vehicle-${vehicle.licensePlate}`] || ""
                
                vehicleData.codEntidadeOrigem = vehicleData.codSubFrota;
                vehicleData.codEntidadeDestino = vehicleData.codVeiculo;
                vehicleData.codTipoEntidadeOrigem = 6;
                vehicleData.codTipoEntidadeDestino = 7
                
                // Adicionar campos de criptografia
                vehicleData.CryptValue = vehicle.cryptValue;
                vehicleData.CryptValueParent = vehicle.cryptValueParent; // cryptValue da SubFrota pai
                vehicleData.CryptKeys = vehicle.cryptKeys;
                
                // Adicionar campos genéricos usando a função
                const vehicleDataWithGenericFields = this.addGenericFields(vehicleData)
                listLimiteVeiculoTO.push(vehicleDataWithGenericFields)
              }
            }
          })
        }
      })
    
    return {
      "DscObservacao": this.getObservacaoGeral(),
      "ListLimiteFrotaTO": listLimiteFrotaTO.length > 0 ? listLimiteFrotaTO : undefined,
      "ListLimiteSubFrotaTO": listLimiteSubFrotaTO.length > 0 ? listLimiteSubFrotaTO : undefined,
      "ListLimiteVeiculoTO": listLimiteVeiculoTO.length > 0 ? listLimiteVeiculoTO : undefined,
      "ListLimitePeriodicaSubFrotaTO": [],
      "ListLimitePeriodicaFrotaTO": [],
      "ListLimitePeriodicaVeiculoTO": [],
      "CmdName": this.cmdName ? Number(this.cmdName) : undefined
    }
  }

  /**
   * Função genérica para adicionar os campos codGrupoCombustivel e codTipoLimite
   * em qualquer item das listagens de consolidação de saldo
   * @param item - O item da listagem (frota, subfrota ou veículo)
   * @returns O item com os campos genéricos adicionados
   */
  private addGenericFields(item: any): any {
    // Buscar a macro correta: se tipoUsuario = 2 usa posição 0, senão busca pelo CodMacro do item
    let macro = this.tipoUsuario === 2
      ? this.macroData[0]
      : this.macroData.find(m => m.id === item.CodMacro);

    // Fallback para macroData[0] se não encontrar
    if (!macro && this.macroData.length > 0) {
      macro = this.macroData[0];
    }

    return {
      ...item,
      "codGrupoCombustivel": macro?.codGrupoCombustivel,
      "codTipoLimite": macro?.codTipoLimite
    };
  }

  // Método para obter o código do veículo
  private getVehicleCode(licensePlate: string): number {
    // Usar dados originais se disponíveis
    const originalData = this.originalVehicleData[licensePlate]
    if (originalData && originalData.codVeiculo) {
      return originalData.codVeiculo
    }
    
    // Fallback: gerar código baseado na placa
    return parseInt(licensePlate.replace(/[^0-9]/g, '')) || 100000
  }

  // Método para encontrar o ID da frota de uma subfrota
  private findFleetIdForSubFleet(subFleetId: number): number {
    if (this.macroData.length === 0) return 0
    
    for (const fleet of this.macroData[0].fleets) {
      const subFleet = fleet.subFleets.find(sf => sf.id === subFleetId)
      if (subFleet) {
        return subFleet.codFrota
      }
    }
    
    return 0
  }



  // Método para obter observação geral
  private getObservacaoGeral(): string {
    // Se há observação geral, usar ela
    if (this.generalObservation && this.generalObservation.trim() !== '') {
      return this.generalObservation.trim()
    }
    
    // Combinar apenas as observações dos veículos que tiveram ajustes
    const observacoes: string[] = []
    
    // Adicionar observações de veículos com ajustes diretos
    this.adjustmentHistory
      .filter(adj => adj.type === 'vehicle')
      .forEach(adj => {
        const obs = this.notes[adj.id]
        if (obs && obs.trim() !== '') {
          observacoes.push(obs)
        }
      })
    
    // Adicionar observações de veículos que tiveram ajustes individuais em subfrotas
    this.adjustmentHistory
      .filter(adj => adj.type === 'subfleet')
      .forEach(adj => {
        const subFleetId = parseInt(adj.id.replace('subfleet-', ''))
        const subFleet = this.findSubFleetById(subFleetId)
        
        if (subFleet) {
          subFleet.vehicles.forEach(vehicle => {
            const vehicleAdjustment = this.getVehicleAdjustment(vehicle.licensePlate)
            if (vehicleAdjustment > 0) {
              const obs = this.notes[`vehicle-${vehicle.licensePlate}`]
              if (obs && obs.trim() !== '') {
                observacoes.push(obs)
              }
            }
          })
        }
      })
    
    if (observacoes.length === 0) {
      return "Ajuste de saldo realizado via sistema"
    }
    
    return observacoes.join('; ')
  }

  // Métodos auxiliares para o sistema de ajustes em tempo real
  parseCurrencyValue(value: any): number {
    // Se o valor já é um número, retorna ele mesmo
    if (typeof value === 'number') {
      return value
    }
    
    // Se é string, converte
    if (typeof value === 'string') {
      if (!value || value.trim() === '') return 0
      
      // Se é apenas "-", retorna 0
      if (value === '-') return 0
      
      // Verificar se é negativo
      const isNegative = value.startsWith('-')
      
      // Remove todos os caracteres não numéricos exceto vírgula e ponto
      let cleanValue = value.replace(/[^\d.,]/g, '')
      
      // Se após limpar não há números, retorna 0
      if (!cleanValue || cleanValue === '') return 0
      
      // Se não há vírgula, trata como número inteiro
      if (!cleanValue.includes(',')) {
        const numericValue = parseFloat(cleanValue) || 0
        return isNegative ? -numericValue : numericValue
      }
      
      // Se há vírgula, remove pontos e substitui vírgula por ponto
      // Formato brasileiro: 1.234,56 -> 1234.56
      cleanValue = cleanValue.replace(/\./g, '').replace(',', '.')
      
      const numericValue = parseFloat(cleanValue) || 0
      return isNegative ? -numericValue : numericValue
    }
    
    // Para outros tipos, tenta converter para número
    return Number(value) || 0
  }

  private findFleetById(id: number): Fleet | null {
    if (this.macroData.length === 0) return null
    return this.macroData[0].fleets.find(fleet => fleet.id === id) || null
  }

  private findSubFleetById(id: number): SubFleet | null {
    // Primeiro tentar encontrar nos dados carregados
    if (this.macroData.length > 0) {
      for (const fleet of this.macroData[0].fleets) {
        const subFleet = fleet.subFleets.find(sf => sf.id === id)
        if (subFleet) return subFleet
      }
    }
    
    // Se não encontrar, usar o cache de confirmação
    return this.confirmationDataCache.subFleets[id] || null
  }

  private findVehicleById(id: string): Vehicle | null {
    // Primeiro tentar encontrar nos dados carregados
    if (this.macroData.length > 0) {
      for (const fleet of this.macroData[0].fleets) {
        for (const subFleet of fleet.subFleets) {
          const vehicle = subFleet.vehicles.find(v => v.licensePlate === id)
          if (vehicle) return vehicle
        }
      }
    }
    
    // Se não encontrar, usar o cache de confirmação
    return this.confirmationDataCache.vehicles[id] || null
  }



  private initializeOriginalBalance(): void {
    if (this.macroData.length === 0) return
    
    const macro = this.macroData[0]
    if (macro.fleets.length === 0) return
    
    const fleet = macro.fleets[0]
    
    // Salvar saldo original da frota
    this.originalFleetBalance = this.parseCurrencyValue(fleet.currentBalance)
    
    // Inicializar saldos originais de todas as subfrotas
    for (const subFleet of fleet.subFleets) {
      this.originalSubFleetBalances[subFleet.id] = this.parseCurrencyValue(subFleet.currentBalance)
    }
  }

  // Método para debug - exibir histórico de ajustes
  getAdjustmentHistory(): string {
    return this.adjustmentHistory.map(adj => 
      `${adj.type}: ${adj.description} - R$ ${adj.value.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`
    ).join('\n')
  }

  // Método para obter o total de ajustes formatado
  getTotalAdjustmentsFormatted(): string {
    return this.totalAdjustments.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Método para obter ajustes de subfrotas
  private getSubFleetAdjustments(): any[] {
    return this.adjustmentHistory
      .filter(adj => adj.type === 'subfleet')
      .map(adj => {
        const subFleetId = parseInt(adj.id.replace('subfleet-', ''))
        const subFleet = this.findSubFleetById(subFleetId)
        return {
          codigo: subFleetId,
          descricao: subFleet?.description || `Subfrota ${subFleetId}`,
          saldoOriginal: this.originalSubFleetBalances[subFleetId] || 0,
          ajuste: adj.value,
          observacoes: this.notes[adj.id] || ''
        }
      })
  }

  // Método para obter ajustes de veículos
  private getVehicleAdjustments(): any[] {
    return this.adjustmentHistory
      .filter(adj => adj.type === 'vehicle')
      .map(adj => {
        const vehicleId = adj.id.replace('vehicle-', '')
        const vehicle = this.findVehicleById(vehicleId)
        const subFleetId = this.findSubFleetIdForVehicle(vehicleId)
        const subFleet = subFleetId ? this.findSubFleetById(subFleetId) : null
        
        return {
          placa: vehicleId,
          codigoFrota: vehicle?.fleetCode || '',
          modelo: vehicle?.model || '',
          saldoOriginal: vehicle ? this.parseCurrencyValue(vehicle.currentBalance) : 0,
          ajuste: adj.value,
          observacoes: this.notes[adj.id] || '',
          subFrota: {
            codigo: subFleetId,
            descricao: subFleet?.description || ''
          }
        }
      })
  }

  // Métodos para o acordeão de confirmação
  toggleFleetConfirmation(fleetId: number): void {
    const index = this.expandedFleetsConfirmation.indexOf(fleetId)
    if (index > -1) {
      this.expandedFleetsConfirmation.splice(index, 1)
    } else {
      this.expandedFleetsConfirmation.push(fleetId)
    }
  }

  isFleetExpandedConfirmation(fleetId: number): boolean {
    return this.expandedFleetsConfirmation.includes(fleetId)
  }

  // Método para obter o saldo original de uma subfrota
  getOriginalSubFleetBalance(subFleetId: number): number {
    return this.originalSubFleetBalances[subFleetId] || 0
  }

  // Método para obter o saldo original da frota
  getOriginalFleetBalance(fleetId: number): number {
    return this.originalFleetBalance
  }

  // Método para obter o saldo atual da frota para validação (excluindo o ajuste sendo validado)
  getCurrentFleetBalanceForValidation(excludeKey: string): number {
    // Calcular ajustes diretos da frota (se houver)
    const directFleetAdjustments = this.adjustmentHistory
      .filter(adj => adj.id.startsWith('fleet-'))
      .reduce((sum, adj) => sum + adj.value, 0)
    
    // Calcular transferências das subfrotas para a frota, EXCLUINDO o ajuste sendo validado
    const subFleetTransferAdjustments = this.adjustmentHistory
      .filter(adj => adj.id.startsWith('subfleet-') && adj.id !== excludeKey)
      .reduce((sum, adj) => sum - adj.value, 0) // Sinal negativo para inverter
    
    return this.originalFleetBalance + directFleetAdjustments + subFleetTransferAdjustments
  }

  // Método para reverter o saldo da frota para o valor original
  private revertFleetBalanceToOriginal(): void {
    if (this.macroData.length === 0) return
    
    const macro = this.macroData[0]
    if (macro.fleets.length === 0) return
    
    const fleet = macro.fleets[0]
    
    // Restaurar saldo original da frota
    fleet.currentBalance = this.originalFleetBalance.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Método para obter o saldo atual da subfrota para validação (excluindo o ajuste sendo validado)
  getCurrentSubFleetBalanceForValidation(excludeKey: string, subFleetId: number): number {
    // Calcular ajustes diretos da subfrota (soma normal), EXCLUINDO o ajuste sendo validado
    const directSubFleetAdjustments = this.adjustmentHistory
      .filter(adj => adj.id.startsWith('subfleet-') && adj.id === `subfleet-${subFleetId}` && adj.id !== excludeKey)
      .reduce((sum, adj) => sum + adj.value, 0)
    
    // Calcular ajustes dos veículos (transferência inversa), EXCLUINDO o ajuste sendo validado
    const vehicleTransferAdjustments = this.adjustmentHistory
      .filter(adj => {
        if (adj.id.startsWith('vehicle-') && adj.id !== excludeKey) {
          const vehicleSubFleetId = this.findSubFleetIdForVehicle(adj.id.replace('vehicle-', ''))
          return vehicleSubFleetId === subFleetId
        }
        return false
      })
      .reduce((sum, adj) => sum - adj.value, 0) // Sinal negativo para inverter
    
    return this.originalSubFleetBalances[subFleetId] + directSubFleetAdjustments + vehicleTransferAdjustments
  }

  // Método para reverter o saldo da subfrota para o valor original
  private revertSubFleetBalanceToOriginal(vehicleId: string): void {
    const subFleetId = this.findSubFleetIdForVehicle(vehicleId)
    if (!subFleetId) return
    
    if (this.macroData.length === 0) return
    
    const macro = this.macroData[0]
    if (macro.fleets.length === 0) return
    
    const fleet = macro.fleets[0]
    const subFleet = fleet.subFleets.find(sf => sf.id === subFleetId)
    
    if (subFleet && this.originalSubFleetBalances[subFleetId]) {
      // Restaurar saldo original da subfrota
      subFleet.currentBalance = this.originalSubFleetBalances[subFleetId].toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
  }


  // Método para obter o ajuste total de uma subfrota
  getSubFleetTotalAdjustment(subFleetId: number): number {
    return this.adjustmentHistory
      .filter(adj => {
        if (adj.id.startsWith('subfleet-') && adj.id === `subfleet-${subFleetId}`) {
          return true
        }
        if (adj.id.startsWith('vehicle-')) {
          const vehicleSubFleetId = this.findSubFleetIdForVehicle(adj.id.replace('vehicle-', ''))
          return vehicleSubFleetId === subFleetId
        }
        return false
      })
      .reduce((sum, adj) => sum + adj.value, 0)
  }

  // Método para verificar se uma subfrota teve alterações
  hasSubFleetChanges(subFleetId: number): boolean {
    const subFleet = this.findSubFleetById(subFleetId)
    if (!subFleet) return false
    
    // Verifica se a própria subfrota teve ajuste direto (incluindo valores negativos)
    const hasDirectAdjustment = this.adjustmentHistory.some(adj => 
      adj.id === `subfleet-${subFleetId}` && adj.value !== 0
    )
    
    // Verifica se algum veículo da subfrota teve ajuste (incluindo valores negativos)
    const hasVehicleAdjustment = this.adjustmentHistory.some(adj => {
      if (adj.type !== 'vehicle') return false
      
      const vehicleId = adj.id.replace('vehicle-', '')
      const vehicleSubFleetId = this.findSubFleetIdForVehicle(vehicleId)
      return vehicleSubFleetId === subFleetId && adj.value !== 0
    })
    
    return hasDirectAdjustment || hasVehicleAdjustment
  }

  // Método para obter apenas as subfrotas que tiveram alterações
  getSubFleetsWithChanges(fleet: Fleet): SubFleet[] {
    return fleet.subFleets.filter(subFleet => {
      // Verifica se a própria subfrota teve ajuste direto (incluindo valores negativos)
      const hasDirectAdjustment = this.adjustmentHistory.some(adj => 
        adj.id === `subfleet-${subFleet.id}` && adj.value !== 0
      )
      
      // Verifica se algum veículo da subfrota teve ajuste (incluindo valores negativos)
      const hasVehicleAdjustment = this.adjustmentHistory.some(adj => {
        if (adj.type !== 'vehicle') return false
        
        const vehicleId = adj.id.replace('vehicle-', '')
        const vehicleSubFleetId = this.findSubFleetIdForVehicle(vehicleId)
        return vehicleSubFleetId === subFleet.id && adj.value !== 0
      })
      
      return hasDirectAdjustment || hasVehicleAdjustment
    })
  }

  // Método para verificar se uma frota tem alterações (própria frota ou subfrotas)
  hasFleetChanges(fleet: Fleet): boolean {
    // Verifica se a própria frota teve ajuste direto
    const hasFleetDirectAdjustment = this.adjustmentHistory.some(adj =>
      adj.id === `fleet-${fleet.id}` && adj.value !== 0
    )

    if (hasFleetDirectAdjustment) return true

    // Verifica se alguma subfrota teve alteração
    return fleet.subFleets.some(subFleet => {
      // Verifica se a própria subfrota teve ajuste direto (incluindo valores negativos)
      const hasDirectAdjustment = this.adjustmentHistory.some(adj =>
        adj.id === `subfleet-${subFleet.id}` && adj.value !== 0
      )

      // Verifica se algum veículo da subfrota teve ajuste (incluindo valores negativos)
      const hasVehicleAdjustment = this.adjustmentHistory.some(adj => {
        if (adj.type !== 'vehicle') return false

        const vehicleId = adj.id.replace('vehicle-', '')
        const vehicleSubFleetId = this.findSubFleetIdForVehicle(vehicleId)
        return vehicleSubFleetId === subFleet.id && adj.value !== 0
      })

      return hasDirectAdjustment || hasVehicleAdjustment
    })
  }

  // Método para obter o ajuste direto da frota
  getFleetAdjustment(fleetId: number): number {
    const adjustment = this.adjustmentHistory.find(adj => adj.id === `fleet-${fleetId}`)
    return adjustment ? adjustment.value : 0
  }

  // Método para obter as observações da frota
  getFleetNotes(fleetId: number): string {
    return this.notes[`fleet-${fleetId}`] || ''
  }

  // Método para verificar se a frota tem ajuste direto
  hasFleetDirectAdjustment(fleetId: number): boolean {
    return this.adjustmentHistory.some(adj => adj.id === `fleet-${fleetId}` && adj.value !== 0)
  }

  // Método para obter apenas as frotas que têm subfrotas com alterações
  getFleetsWithChanges(): Fleet[] {
    if (!this.macroData[0]?.fleets) return []
    return this.macroData[0].fleets.filter(fleet => this.hasFleetChanges(fleet))
  }

  // Método para verificar se há algum ajuste no histórico
  hasAnyAdjustments(): boolean {
    return this.adjustmentHistory.length > 0
  }

  // Método para avançar para tela de confirmação (botão flutuante)
  goToConfirmation(): void {
    if (this.hasAnyAdjustments()) {
      this.currentStep = 'confirmation'
      // Scroll para o topo da página
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  // Método para obter o ajuste de um veículo específico
  getVehicleAdjustment(vehicleId: string): number {
    const adjustment = this.adjustmentHistory.find(adj => adj.id === `vehicle-${vehicleId}`)
    return adjustment ? adjustment.value : 0
  }

  // Método para obter as observações de um veículo específico
  getVehicleNotes(vehicleId: string): string {
    return this.notes[`vehicle-${vehicleId}`] || ''
  }

  // Método para obter apenas os veículos que tiveram alterações
  getVehiclesWithChanges(subFleet: SubFleet): Vehicle[] {
    // Obter todos os ajustes de veículos que pertencem a esta subfrota
    const vehicleAdjustments = this.adjustmentHistory.filter(adj => {
      if (adj.type !== 'vehicle') return false
      
      const vehicleId = adj.id.replace('vehicle-', '')
      const vehicleSubFleetId = this.findSubFleetIdForVehicle(vehicleId)
      return vehicleSubFleetId === subFleet.id
    })
    
    const vehiclesWithChanges: Vehicle[] = []
    
    for (const adjustment of vehicleAdjustments) {
      const vehicleId = adjustment.id.replace('vehicle-', '')
      
      // Primeiro tentar encontrar nos veículos da subfrota
      let vehicle = subFleet.vehicles.find(v => v.licensePlate === vehicleId)
      
      // Se não encontrar, tentar no cache
      if (!vehicle) {
        vehicle = this.confirmationDataCache.vehicles[vehicleId]
      }
      
      // Se encontrou o veículo, adicionar (mesmo que o ajuste seja 0)
      if (vehicle) {
        vehiclesWithChanges.push(vehicle)
      }
    }
    
    return vehiclesWithChanges
  }

  // Método para verificar se deve exibir a listagem de veículos para uma subfrota específica
  // Considera indOcultarVeiculos e mostraBotaoAcao (se mostraBotaoAcao === false, não mostra veículos)
  shouldShowVehicles(subFleetId: number): boolean {
    // Encontrar a subfrota específica
    if (this.macroData.length > 0) {
      const macro = this.macroData[0];
      for (const fleet of macro.fleets) {
        const subFleet = fleet.subFleets.find(sf => sf.id === subFleetId);
        if (subFleet) {
          // Não mostrar veículos se indOcultarVeiculos === true OU mostraBotaoAcao === false
          const shouldShow = !subFleet.indOcultarVeiculos && subFleet.mostraBotaoAcao !== false;
          return shouldShow;
        }
      }
    }
    return true; // Por padrão, mostrar veículos se não encontrar a subfrota
  }

  // Método para redirecionar para a tela antiga
  goToOldScreen(): void {
    const url = environment.sistemas.reportportalctfux + `login.aspx?cmdName=${this.cmdName}&userGuid=${this.userGuid}&Formato=Redirecionar`;
    window.open(url, '_blank');
  }

  // Método para obter o saldo original de um veículo
  getOriginalVehicleBalance(licensePlate: string): number {
    // Se temos dados originais salvos, retorna eles
    if (this.originalVehicleData[licensePlate]) {
      return this.originalVehicleData[licensePlate].valSaldoOriginal || 0
    }
    
    // Caso contrário, procura o veículo atual e retorna o saldo atual
    // (assumindo que o saldo atual é o original se não foi modificado)
    const vehicle = this.findVehicleByLicensePlate(licensePlate)
    if (vehicle) {
      return this.parseCurrencyValue(vehicle.currentBalance)
    }
    
    return 0
  }

  // Método para obter o saldo atual do veículo (original + ajuste)
  getVehicleCurrentBalance(licensePlate: string): string {
    const originalBalance = this.getOriginalVehicleBalance(licensePlate)
    const adjustment = this.parseCurrencyValue(this.adjustments[`vehicle-${licensePlate}`] || '0')
    const currentBalance = originalBalance + adjustment
    
    return currentBalance.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Método auxiliar para encontrar veículo por placa
  private findVehicleByLicensePlate(licensePlate: string): Vehicle | null {
    if (this.macroData.length === 0) return null
    
    const macro = this.macroData[0]
    if (macro.fleets.length === 0) return null
    
    const fleet = macro.fleets[0]
    
    for (const subFleet of fleet.subFleets) {
      const vehicle = subFleet.vehicles.find(v => v.licensePlate === licensePlate)
      if (vehicle) return vehicle
    }
    
    return null
  }
}
